import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Store, Link2, Unlink, RefreshCw, ExternalLink, CheckCircle2,
  AlertTriangle, Settings2, Save, Eye, Search, Image as ImageIcon,
  Upload, Edit3, ListChecks, Wand2, Rocket, Archive, ShoppingBag,
  ShieldAlert, ShieldCheck, Stethoscope, Bomb, Repeat, Mail,
} from "lucide-react";
import { apiClient } from "../../lib/api";

/**
 * Admin → Integrations → Etsy (Phase 1)
 *
 * Foundation tab — connect / disconnect an Etsy shop via OAuth 2.0
 * PKCE, view connection status, and manage the sync settings
 * (markup %, inventory cap, colour limit, profile IDs).
 *
 * Product-selection / preview / draft-creation / publish / webhook
 * pieces land in subsequent phases behind the same feature flag.
 */
// Poll an Etsy background job every 1.5s until it settles.
// `onProgress({completed, total, message, counts, results})` streams live.
// Returns the final job doc (with `summary`). Throws on `status: error`.
async function pollEtsyJob(jobId, pw, onProgress) {
  const started = Date.now();
  const MAX_MS = 30 * 60 * 1000;  // 30 min ceiling
  for (;;) {
    if (Date.now() - started > MAX_MS) throw new Error("Job polling timed out after 30 min");
    let doc;
    try {
      const { data } = await apiClient.get(`/admin/etsy/jobs/${jobId}`,
        { headers: { "X-Admin-Password": pw } });
      doc = data;
    } catch (e) {
      // Transient poll failure — wait and retry. Only bail on 404.
      if (e?.response?.status === 404) throw new Error("Job not found or expired");
      await new Promise((r) => setTimeout(r, 3000));
      continue;
    }
    if (onProgress) onProgress(doc);
    if (doc.status === "done") return doc;
    if (doc.status === "error") throw new Error(doc.error || "Job failed");
    await new Promise((r) => setTimeout(r, 1500));
  }
}

export default function EtsyTab({ pw }) {
  const [status, setStatus] = useState(null);
  const [settings, setSettings] = useState(null);
  const [profiles, setProfiles] = useState(null);   // {shipping_profiles, return_policies, production_partners}
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/admin/etsy/status", {
        headers: { "X-Admin-Password": pw },
      });
      setStatus(data);
      setSettings(data.settings);
      if (data.connected) {
        try {
          const { data: p } = await apiClient.get("/admin/etsy/profiles", {
            headers: { "X-Admin-Password": pw },
          });
          setProfiles(p);
        } catch (e) {
          // Non-fatal — dropdowns fall back to text inputs on error.
          setProfiles(null);
        }
      } else {
        setProfiles(null);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load Etsy status");
    } finally { setLoading(false); }
  }, [pw]);

  useEffect(() => { reload(); }, [reload]);

  // Show a toast when the OAuth callback bounces us back here.
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const outcome = p.get("etsy");
    if (outcome === "connected") {
      toast.success("Etsy shop connected!");
      reload();
      const url = new URL(window.location.href);
      url.searchParams.delete("etsy"); url.searchParams.delete("msg");
      window.history.replaceState({}, "", url.toString());
    } else if (outcome === "error") {
      toast.error(p.get("msg") || "Etsy connection failed");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async () => {
    setConnecting(true);
    try {
      const { data } = await apiClient.post("/admin/etsy/oauth/connect", {}, {
        headers: { "X-Admin-Password": pw },
      });
      window.location.href = data.authorize_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start Etsy OAuth");
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect the Etsy shop? Existing listing mappings are kept, but syncs will stop until you reconnect.")) return;
    try {
      await apiClient.post("/admin/etsy/oauth/disconnect", {}, {
        headers: { "X-Admin-Password": pw },
      });
      toast.success("Etsy shop disconnected");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Disconnect failed");
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const { data } = await apiClient.put("/admin/etsy/settings", settings, {
        headers: { "X-Admin-Password": pw },
      });
      setSettings(data);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (loading || !status) return <div className="text-zinc-500 font-mono text-sm">Loading Etsy status…</div>;

  return (
    <div data-testid="etsy-tab" className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="font-display text-3xl uppercase flex items-center gap-3">
          <Store size={22} className="text-[var(--tc-red)]" /> Etsy Integration
        </h2>
        <div className="font-mono text-[11px] text-zinc-500 mt-1">
          Sales-channel sync — sync active custom products to Etsy as drafts, import Etsy orders into this admin.
        </div>
      </div>

      {/* Connection card */}
      <div className="surface-card p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex-1 min-w-[280px]">
            <div className="font-mono text-[10px] tracking-widest text-zinc-500 mb-2">CONNECTION</div>
            {!status.enabled ? (
              <ConnectionState
                icon={<AlertTriangle size={18} className="text-amber-400" />}
                label="Integration disabled"
                detail="Set ETSY_INTEGRATION_ENABLED=1 in backend/.env and restart to enable this integration."
              />
            ) : !status.configured ? (
              <ConnectionState
                icon={<AlertTriangle size={18} className="text-amber-400" />}
                label="Credentials missing"
                detail="Set ETSY_KEYSTRING, ETSY_SHARED_SECRET, and ETSY_REDIRECT_URI in backend/.env, then restart."
              />
            ) : status.connected ? (
              <ConnectionState
                icon={<CheckCircle2 size={18} className="text-emerald-400" />}
                label={status.shop_name ? `Connected: ${status.shop_name}` : "Connected"}
                detail={
                  <>
                    Shop ID <span className="text-white">{status.shop_id || "—"}</span>
                    {" · "}User <span className="text-white">{status.etsy_user_id || "—"}</span>
                    {" · "}Scopes <span className="text-white">{(status.scopes || []).join(" ")}</span>
                    {status.needs_reconnect_for_scopes && (
                      <div className="mt-2 border border-amber-500/50 bg-amber-500/10 p-2 text-amber-300"
                        data-testid="etsy-scopes-warning">
                        <div className="flex items-center gap-1 font-mono text-[10px] tracking-widest">
                          <AlertTriangle size={12} /> RECONNECT NEEDED
                        </div>
                        <div className="text-xs mt-1">
                          Missing scope(s): <span className="text-white font-mono">{status.scopes_missing.join(" ")}</span>.
                          {" "}Etsy locks scopes at connect time. Click <strong>Disconnect</strong> above, then <strong>Connect Etsy</strong>{" "}
                          and approve the new permissions — required for Nuke &amp; Rebuild (DELETE listings).
                        </div>
                      </div>
                    )}
                  </>
                }
              />
            ) : (
              <ConnectionState
                icon={<Link2 size={18} className="text-zinc-400" />}
                label="Not connected"
                detail="Click Connect Etsy to authorize your shop via OAuth 2.0 (PKCE). Tokens are stored server-side only."
              />
            )}
          </div>
          <div className="flex flex-col gap-2">
            {status.connected ? (
              <button
                data-testid="etsy-disconnect-btn"
                onClick={disconnect}
                className="btn-tc-secondary text-xs flex items-center gap-2"
              >
                <Unlink size={13} /> Disconnect
              </button>
            ) : (
              <button
                data-testid="etsy-connect-btn"
                onClick={connect}
                disabled={!status.enabled || !status.configured || connecting}
                className="btn-tc-primary text-xs flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ExternalLink size={13} /> {connecting ? "Redirecting…" : "Connect Etsy"}
              </button>
            )}
            <button
              onClick={reload}
              className="btn-tc-secondary text-[10px] flex items-center gap-1 justify-center"
            >
              <RefreshCw size={11} /> Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Settings */}
      <div className="surface-card p-6" data-testid="etsy-settings-card">
        <div className="flex items-center justify-between mb-4">
          <div className="font-mono text-[10px] tracking-widest text-zinc-500 flex items-center gap-2">
            <Settings2 size={13} /> SYNC SETTINGS
          </div>
          <button
            data-testid="etsy-settings-save"
            onClick={saveSettings}
            disabled={saving || !settings}
            className="btn-tc-primary text-xs flex items-center gap-2 disabled:opacity-50"
          >
            <Save size={12} /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <NumberField
            label="Etsy channel markup (%)"
            hint="Added on top of the calculated price to cover Etsy fees + your margin. Default 30%."
            value={settings.markup_pct}
            onChange={(v) => setSettings((s) => ({ ...s, markup_pct: v }))}
            testId="etsy-markup-pct"
            min={0} max={500}
          />
          <NumberField
            label="Inventory cap per variant"
            hint="Max quantity we'll send to Etsy for each size/colour combination."
            value={settings.inventory_cap_per_variant}
            onChange={(v) => setSettings((s) => ({ ...s, inventory_cap_per_variant: v }))}
            testId="etsy-inventory-cap"
            min={0} max={999}
          />
          <NumberField
            label="Max colours per listing"
            hint="If the product has more colours we'll pick the top N by inventory (or admin-selected). Etsy caps variations at 70."
            value={settings.max_colours_per_listing}
            onChange={(v) => setSettings((s) => ({ ...s, max_colours_per_listing: v }))}
            testId="etsy-max-colours"
            min={1} max={70}
          />
          <SelectField
            label="Price rounding"
            value={settings.price_rounding}
            onChange={(v) => setSettings((s) => ({ ...s, price_rounding: v }))}
            testId="etsy-price-rounding"
            options={[
              { value: ".99", label: "Round to .99 (e.g. $34.99)" },
              { value: "none", label: "No rounding (use raw calc)" },
            ]}
          />
          <ProfileField
            label="Etsy shipping profile"
            hint={profiles ? "Pick from profiles fetched live from your Etsy shop." : "Connect Etsy first — this becomes a dropdown once connected."}
            value={settings.shipping_profile_id}
            onChange={(v) => setSettings((s) => ({ ...s, shipping_profile_id: v }))}
            testId="etsy-shipping-profile"
            options={profiles?.shipping_profiles}
          />
          <ProfileField
            label="Etsy return policy"
            hint={profiles ? "Pick from policies fetched live from your Etsy shop." : "Connect Etsy first — this becomes a dropdown once connected."}
            value={settings.return_policy_id}
            onChange={(v) => setSettings((s) => ({ ...s, return_policy_id: v }))}
            testId="etsy-return-policy"
            options={profiles?.return_policies}
          />
          <ProfileField
            label="Etsy production partner (optional)"
            hint={profiles ? "Pick from partners fetched live from your Etsy shop (blank = handled in-house)." : "Optional. Leave blank if you don't use production partners."}
            value={settings.processing_profile_id}
            onChange={(v) => setSettings((s) => ({ ...s, processing_profile_id: v }))}
            testId="etsy-processing-profile"
            options={profiles?.production_partners}
          />
          <label className="flex items-center gap-3 mt-2 font-mono text-xs text-zinc-300">
            <input
              type="checkbox"
              checked={settings.publish_confirmation_required}
              onChange={(e) => setSettings((s) => ({ ...s, publish_confirmation_required: e.target.checked }))}
              className="accent-[var(--tc-red)]"
              data-testid="etsy-publish-confirm"
            />
            Require confirmation before publishing drafts to Etsy
          </label>
          <label className="flex items-center gap-3 mt-1 font-mono text-xs text-zinc-300">
            <input
              type="checkbox"
              checked={settings.auto_sync_on_create ?? true}
              onChange={(e) => setSettings((s) => ({ ...s, auto_sync_on_create: e.target.checked }))}
              className="accent-[var(--tc-red)]"
              data-testid="etsy-auto-sync-on-create"
            />
            Auto-push new custom products to Etsy on create (with size + colour chart images)
          </label>
        </div>
      </div>

      {/* iter119j — grouped sections with scope pills so the admin
          knows which panel affects which product type. */}
      {status.connected && (
        <EtsySection
          title="Customizable Products"
          scope="customizable"
          subtitle="Custom (SanMar) T-shirts pushed to Etsy as personalisation-enabled drafts."
          testId="etsy-section-customizable"
          defaultOpen>
          <PreviewAllProducts pw={pw} />
          <CustomIpAuditPanel pw={pw} />
        </EtsySection>
      )}

      {status.connected && (
        <EtsySection
          title="Ready-Made Designs"
          scope="ready-made"
          subtitle="Pre-designed products pushed to Etsy with dynamic IP-obfuscation."
          testId="etsy-section-ready-made"
          defaultOpen={false}>
          <ReadyMadeEtsyPanel pw={pw} />
        </EtsySection>
      )}

      {status.connected && (
        <EtsySection
          title="Shop-Wide Tools"
          scope="all"
          subtitle="Compliance sweep, live-state mirror, diagnostics — applies to every listing regardless of type."
          testId="etsy-section-all"
          defaultOpen>
          <OffPlatformCompliancePanel pw={pw} />
          <EtsyDiagnosticsPanel pw={pw} />
        </EtsySection>
      )}

      {status.connected && (
        <EtsySection
          title="Orders & Sync History"
          scope="all"
          subtitle="Incoming Etsy orders + audit trail for every draft/patch."
          testId="etsy-section-orders"
          defaultOpen={false}>
          <EtsyOrdersPanel pw={pw} />
          <SyncLogPanel pw={pw} />
        </EtsySection>
      )}

      {/* Phase note */}
      <div className="surface-card p-6 border-l-4 border-l-[var(--tc-red)]">
        <div className="font-mono text-[10px] tracking-widest text-[var(--tc-red)] mb-2">ETSY INTEGRATION · LIVE</div>
        <p className="text-sm text-zinc-400 leading-relaxed">
          Sections above are grouped by scope: <span className="text-emerald-400 font-mono">CUSTOMIZABLE</span>{" "}
          panels affect SanMar-based custom products only,{" "}
          <span className="text-cyan-400 font-mono">READY-MADE</span> panels affect pre-designed listings only,{" "}
          <span className="text-amber-400 font-mono">ALL</span> panels apply to every listing.
        </p>
      </div>
    </div>
  );
}

// ============================================================================
// iter119j — Grouped section wrapper with scope pill + collapse
// ============================================================================
function EtsySection({ title, scope, subtitle, children, defaultOpen = true, testId }) {
  const [open, setOpen] = useState(defaultOpen);
  const scopeStyle = {
    customizable: "border-emerald-500/40 text-emerald-300 bg-emerald-500/10",
    "ready-made": "border-cyan-500/40 text-cyan-300 bg-cyan-500/10",
    all:          "border-amber-500/40 text-amber-300 bg-amber-500/10",
  }[scope] || "border-zinc-700 text-zinc-400";
  return (
    <div className="surface-card overflow-hidden" data-testid={testId}>
      <button type="button" onClick={() => setOpen((v) => !v)}
        className="w-full flex items-start justify-between gap-4 p-4 text-left hover:bg-white/[0.02] transition-colors"
        data-testid={`${testId}-toggle`}>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className={`inline-block border px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest ${scopeStyle}`}>
              {scope === "customizable" ? "Customizable" :
                scope === "ready-made" ? "Ready-Made" : "All Products"}
            </span>
            <h3 className="font-ui text-base font-bold text-white">{title}</h3>
          </div>
          {subtitle && (
            <div className="text-[11px] text-zinc-500">{subtitle}</div>
          )}
        </div>
        <div className="font-mono text-[10px] text-zinc-400 mt-1">
          {open ? "▾" : "▸"}
        </div>
      </button>
      {open && (
        <div className="p-4 pt-0 space-y-4 border-t border-[#1a1a1a]">
          {children}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Preview All Products (Phase 2 · dry-run)
// ============================================================================

/**
 * Table of every active catalogue product with the calculated Etsy
 * price, colour picks, and validation status. Read-only for now —
 * Phase 3 (draft creation) reads from `selected` for bulk actions.
 */
function PreviewAllProducts({ pw }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);  // {product, imageUrl}

  const reload = useCallback(async ({ silent = true } = {}) => {
    setLoading(true);
    try {
      const { data: d } = await apiClient.get("/admin/etsy/preview", {
        headers: { "X-Admin-Password": pw },
      });
      setData(d);
      if (!silent) {
        const s = d?.summary || {};
        toast.success(`Preview refreshed — ${s.ready || 0} ready · ${s.missing_info || 0} missing info`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally { setLoading(false); }
  }, [pw]);

  useEffect(() => { reload({ silent: true }); }, [reload]);

  const products = data?.products || [];
  const summary = data?.summary || {};

  const filtered = products.filter((p) => {
    if (statusFilter !== "all" && p.status !== statusFilter) return false;
    if (q.trim()) {
      const s = q.toLowerCase();
      if (!(
        (p.name || "").toLowerCase().includes(s)
        || (p.sku || "").toLowerCase().includes(s)
        || (p.category || "").toLowerCase().includes(s)
      )) return false;
    }
    return true;
  });

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const toggleAll = () => setSelected((prev) => {
    if (prev.size === filtered.length) return new Set();
    return new Set(filtered.map((p) => p.id));
  });

  const readySelected = filtered.filter((p) => selected.has(p.id) && p.status !== "missing_info");
  const draftSelected = filtered.filter((p) => selected.has(p.id) && p.status === "draft");

  const [progress, setProgress] = useState(null);
  const [forceResync, setForceResync] = useState(false);

  const runDrafts = async () => {
    if (readySelected.length === 0) {
      toast.error("Pick at least one product without warnings first.");
      return;
    }
    const total = readySelected.length;
    if (!window.confirm(
      `Push ${total} product(s) to Etsy as drafts?\n\n`
      + (forceResync
        ? "FORCE RE-SYNC ON — sync_hash idempotency bypassed. Every listing will be re-patched even if nothing has changed on our side."
        : "sync_hash guard is ON — listings with unchanged payloads will be skipped as 'No changes'. Toggle Force re-sync to bypass."),
    )) return;
    setBusy(true);
    setProgress({ completed: 0, total, message: "Starting..." });
    try {
      const { data: kickoff } = await apiClient.post("/admin/etsy/drafts",
        { product_ids: readySelected.map((p) => p.id), force: forceResync },
        { headers: { "X-Admin-Password": pw } });
      const doc = await pollEtsyJob(kickoff.job_id, pw, (d) => {
        setProgress({
          completed: d.completed || 0,
          total: d.total || total,
          message: d.message || "",
        });
      });
      const c = doc.summary?.counts || doc.counts || {};
      const pieces = Object.entries(c).map(([k, v]) => `${v} ${k}`).join(" · ");
      toast.success(`Draft sync done: ${pieces || "0 processed"}`, { duration: 15000 });
      await reload();
    } catch (e) {
      toast.error(e?.message || e?.response?.data?.detail || "Draft sync failed");
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  const savePrimaryImage = async (product, patch) => {
    try {
      await apiClient.put(`/admin/etsy/product/${product.id}`, patch,
        { headers: { "X-Admin-Password": pw } });
      toast.success(`Etsy listing updated for ${product.name}`);
      setEditing(null);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const bulkPopulatePrimaryImages = async () => {
    const missing = products.filter((p) => (p.warnings || []).some((w) => w.startsWith("Missing etsy_primary_image"))).length;
    if (!window.confirm(
      `Fill Etsy primary image from the product thumbnail for ${missing || "all missing"} product(s)?\n\n`
      + "This only touches products that don't have an Etsy primary image set yet. "
      + "You can still open each row afterward and override with a real customized shot.",
    )) return;
    setBusy(true);
    try {
      const { data: r } = await apiClient.post("/admin/etsy/products/bulk-populate-primary-image",
        {}, { headers: { "X-Admin-Password": pw } });
      toast.success(`Populated ${r.populated} product(s)${r.skipped_no_thumbnail ? ` · ${r.skipped_no_thumbnail} skipped (no thumbnail)` : ""}`);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk populate failed");
    } finally { setBusy(false); }
  };

  const runPublish = async () => {
    if (draftSelected.length === 0) {
      toast.error("Pick at least one product that's already a draft on Etsy.");
      return;
    }
    const total = draftSelected.length;
    if (!window.confirm(
      `Publish ${total} draft(s) to your live Etsy shop?\n\n`
      + "The listing state flips draft → active — buyers can find and purchase these immediately.",
    )) return;
    setBusy(true);
    setProgress({ completed: 0, total, message: "Starting..." });
    try {
      const { data: kickoff } = await apiClient.post("/admin/etsy/publish",
        { product_ids: draftSelected.map((p) => p.id) },
        { headers: { "X-Admin-Password": pw } });
      const doc = await pollEtsyJob(kickoff.job_id, pw, (d) => {
        setProgress({
          completed: d.completed || 0,
          total: d.total || total,
          message: d.message || "",
        });
      });
      const c = doc.summary?.counts || doc.counts || {};
      const pieces = Object.entries(c).map(([k, v]) => `${v} ${k}`).join(" · ");
      toast.success(`Publish done: ${pieces || "no changes"}`, { duration: 15000 });
      await reload();
    } catch (e) {
      toast.error(e?.message || e?.response?.data?.detail || "Publish failed");
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  const runReconcile = async () => {
    if (!window.confirm(
      "Deactivate every Etsy listing whose product is archived on our side?\n\n"
      + "Listings are set to `inactive` (hidden from buyers but preserved for reactivation later). "
      + "This also runs nightly automatically.",
    )) return;
    setBusy(true);
    try {
      const { data: r } = await apiClient.post("/admin/etsy/reconcile-archived",
        {}, { headers: { "X-Admin-Password": pw } });
      toast.success(`Reconcile done — ${r.deactivated} deactivated · ${r.errors} error(s)`);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reconcile failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="surface-card p-6" data-testid="etsy-preview-card">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="font-mono text-[10px] tracking-widest text-zinc-500 flex items-center gap-2">
          <Eye size={13} /> PREVIEW ALL PRODUCTS
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            data-testid="etsy-bulk-populate-primary-btn"
            onClick={bulkPopulatePrimaryImages}
            disabled={busy}
            className="btn-tc-secondary text-xs flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            title="Fill etsy_primary_image from each product's thumbnail — only for products missing one."
          >
            <Wand2 size={13} />
            Bulk fill primary images
          </button>
          <button
            data-testid="etsy-reconcile-archived-btn"
            onClick={runReconcile}
            disabled={busy}
            className="btn-tc-secondary text-xs flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            title="Deactivate Etsy listings whose products got archived on our side. Also runs nightly."
          >
            <Archive size={13} />
            Reconcile archived
          </button>
          <button
            data-testid="etsy-create-drafts-btn"
            onClick={runDrafts}
            disabled={busy || readySelected.length === 0}
            className="btn-tc-primary text-xs flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            title={readySelected.length === 0
              ? "Select at least one product without warnings"
              : `Create/update Etsy drafts for ${readySelected.length} product(s)`}
          >
            <Upload size={13} />
            {busy ? "Syncing…" : `Create/Update Drafts (${readySelected.length})`}
          </button>
          <button
            data-testid="etsy-publish-btn"
            onClick={runPublish}
            disabled={busy || draftSelected.length === 0}
            className="btn-tc-primary text-xs flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            title={draftSelected.length === 0
              ? "Select at least one product that's currently a draft on Etsy"
              : `Publish ${draftSelected.length} draft(s) — flips state to active on Etsy`}
          >
            <Rocket size={13} />
            {busy ? "Publishing…" : `Publish (${draftSelected.length})`}
          </button>
          <label className="flex items-center gap-2 ml-2 font-mono text-[10px] text-zinc-400"
            title="Bypass the sync_hash idempotency check. Use when a previous sync stored the wrong fingerprint (e.g. after a chart-upload bug fix).">
            <input
              type="checkbox"
              checked={forceResync}
              onChange={(e) => setForceResync(e.target.checked)}
              className="accent-[var(--tc-red)]"
              data-testid="etsy-force-resync"
            />
            Force re-sync (bypass sync_hash)
          </label>
          {progress && (
            <div className="text-[10px] font-mono text-cyan-300 self-center ml-2"
              data-testid="etsy-preview-progress">
              {progress.completed}/{progress.total} - {progress.message}
            </div>
          )}
          <button
            data-testid="etsy-preview-refresh"
            onClick={() => reload({ silent: false })}
            className="btn-tc-secondary text-[10px] flex items-center gap-1"
          >
            <RefreshCw size={11} /> Refresh
          </button>
        </div>
      </div>

      {/* Summary chips */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-5" data-testid="etsy-preview-summary">
        <SummaryChip label="Total" value={summary.total || 0} active={statusFilter === "all"} onClick={() => setStatusFilter("all")} />
        <SummaryChip label="Ready" value={summary.ready || 0} tone="emerald" active={statusFilter === "ready"} onClick={() => setStatusFilter("ready")} />
        <SummaryChip label="Missing info" value={summary.missing_info || 0} tone="amber" active={statusFilter === "missing_info"} onClick={() => setStatusFilter("missing_info")} />
        <SummaryChip label="Draft" value={summary.draft || 0} tone="blue" active={statusFilter === "draft"} onClick={() => setStatusFilter("draft")} />
        <SummaryChip label="Active" value={summary.active || 0} tone="emerald" active={statusFilter === "active"} onClick={() => setStatusFilter("active")} />
        <SummaryChip label="Errors" value={summary.sync_error || 0} tone="red" active={statusFilter === "sync_error"} onClick={() => setStatusFilter("sync_error")} />
      </div>

      {/* Search */}
      <div className="mb-3 relative">
        <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
        <input
          type="text" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Filter by name, SKU or category…"
          data-testid="etsy-preview-search"
          className="w-full bg-black border border-[#222] focus:border-[var(--tc-red)] pl-9 pr-3 py-2 text-sm outline-none"
        />
      </div>

      {loading ? (
        <div className="text-zinc-500 font-mono text-sm py-8 text-center">Loading preview…</div>
      ) : filtered.length === 0 ? (
        <div className="text-zinc-500 font-mono text-sm py-8 text-center">
          {products.length === 0 ? "No active custom products in the catalogue." : "No products match this filter."}
        </div>
      ) : (
        <div className="overflow-x-auto border border-[#222]" data-testid="etsy-preview-table">
          <table className="w-full text-sm">
            <thead className="bg-[#050505] border-b border-[#222] sticky top-0">
              <tr className="font-mono text-[10px] tracking-widest text-zinc-500">
                <th className="p-3 text-left w-8">
                  <input
                    type="checkbox"
                    checked={selected.size > 0 && selected.size === filtered.length}
                    onChange={toggleAll}
                    className="accent-[var(--tc-red)]"
                    data-testid="etsy-preview-select-all"
                  />
                </th>
                <th className="p-3 text-left">PRODUCT</th>
                <th className="p-3 text-left">CATEGORY</th>
                <th className="p-3 text-right">BASE</th>
                <th className="p-3 text-right">CUSTOMIZED</th>
                <th className="p-3 text-right">ETSY PRICE</th>
                <th className="p-3 text-left">COLOURS</th>
                <th className="p-3 text-left">SIZES</th>
                <th className="p-3 text-left">STATUS</th>
                <th className="p-3 text-left w-16">EDIT</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} className="border-b border-[#222] hover:bg-[#0a0a0a]"
                    data-testid={`etsy-product-${p.id}`}>
                  <td className="p-3">
                    <input
                      type="checkbox"
                      checked={selected.has(p.id)}
                      onChange={() => toggle(p.id)}
                      className="accent-[var(--tc-red)]"
                      data-testid={`etsy-product-select-${p.id}`}
                    />
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      {p.image ? (
                        <img src={p.image} alt="" className="w-10 h-10 object-cover border border-[#222] flex-shrink-0" />
                      ) : (
                        <div className="w-10 h-10 border border-[#222] bg-black flex items-center justify-center flex-shrink-0">
                          <ImageIcon size={14} className="text-zinc-600" />
                        </div>
                      )}
                      <div>
                        <div className="font-bold text-white">{p.name || "—"}</div>
                        <div className="font-mono text-[10px] text-zinc-500">SKU {p.sku || "—"} · tax {p.taxonomy_id ?? "—"}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 font-mono text-[10px] text-zinc-400 uppercase">{p.category || "—"}</td>
                  <td className="p-3 text-right font-mono text-zinc-400">${p.base_price?.toFixed(2)}</td>
                  <td className="p-3 text-right font-mono text-zinc-300">${p.customized_price?.toFixed(2)}</td>
                  <td className="p-3 text-right font-mono font-bold text-white">${p.etsy_price?.toFixed(2)}</td>
                  <td className="p-3 font-mono text-[11px] text-zinc-400">
                    {p.colours_selected.length}/{p.colour_count_total}
                  </td>
                  <td className="p-3 font-mono text-[11px] text-zinc-400">{p.size_count}</td>
                  <td className="p-3">
                    <StatusBadge status={p.status} />
                    {p.warnings?.length > 0 && (
                      <div className="mt-1 text-[10px] text-amber-400 leading-tight" title={p.warnings.join(" · ")}>
                        {p.warnings.slice(0, 2).join(" · ")}
                        {p.warnings.length > 2 && ` +${p.warnings.length - 2}`}
                      </div>
                    )}
                    {p.status === "sync_error" && p.last_error && (
                      <div className="mt-1 text-[10px] text-red-400 leading-tight max-w-[220px] truncate"
                        title={p.last_error}
                        data-testid={`etsy-product-error-${p.id}`}>
                        {p.last_error.replace(/^HTTP \d+:\s*/, "").slice(0, 60)}
                      </div>
                    )}
                    {p.etsy_state && p.etsy_state !== p.status && (
                      <div className="mt-1 text-[10px] text-cyan-400 leading-tight"
                        title={`Live Etsy state (last checked ${p.etsy_state_last_checked_at || "—"})`}
                        data-testid={`etsy-product-live-state-${p.id}`}>
                        Live: {p.etsy_state.toUpperCase()}
                      </div>
                    )}
                  </td>
                  <td className="p-3">
                    <button
                      onClick={() => setEditing({ product: p, imageUrl: p.image || "" })}
                      title="Set the Etsy primary image (a finished customized shot)"
                      data-testid={`etsy-product-edit-${p.id}`}
                      className="text-zinc-400 hover:text-white transition-colors"
                    >
                      <Edit3 size={13} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected.size > 0 && (
        <div className="mt-4 flex items-center justify-between gap-3 flex-wrap"
             data-testid="etsy-preview-bulk-bar">
          <div className="font-mono text-[11px] text-zinc-300">
            {selected.size} selected — <span className="text-emerald-400">{readySelected.length} ready</span> · {selected.size - readySelected.length} blocked by warnings
          </div>
          <button
            onClick={() => setSelected(new Set())}
            className="btn-tc-secondary text-[10px]"
            data-testid="etsy-preview-clear-selection"
          >Clear selection</button>
        </div>
      )}

      {editing && (
        <EtsyListingEditor
          product={editing.product}
          initial={editing.imageUrl}
          onClose={() => setEditing(null)}
          onSave={savePrimaryImage}
        />
      )}
    </div>
  );
}

function _EtsyEditorTabBtn({ id, label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={`etsy-editor-tab-${id}`}
      className={`px-4 py-2 text-xs font-mono tracking-widest border-b-2 transition ${
        active ? "border-[var(--tc-red)] text-white" : "border-transparent text-zinc-500 hover:text-white"}`}
    >{label}</button>
  );
}


// ---- Etsy listing editor modal --------------------------------------------
// Lets the admin fine-tune (per product):
//   * Primary image URL (finished-print photo)
//   * Tags (up to 13, 20 chars each — auto-defaults from category)
//   * Custom personalization "options" (up to 5 questions —
//     Text box, Dropdown list, or File upload — matching Etsy's
//     Item options UI 1:1)
// Sent to PUT /api/admin/etsy/product/{id} in one payload.
function EtsyListingEditor({ product, initial, onClose, onSave }) {
  const [tab, setTab] = useState("image");
  const [url, setUrl] = useState(initial || product?.etsy_primary_image || "");
  const [tags, setTags] = useState(
    Array.isArray(product?.etsy_tags) && product.etsy_tags.length > 0
      ? product.etsy_tags
      : (product?.suggested_tags || []),
  );
  const [tagInput, setTagInput] = useState("");
  const [questions, setQuestions] = useState(
    Array.isArray(product?.etsy_personalization_questions) && product.etsy_personalization_questions.length > 0
      ? product.etsy_personalization_questions
      : [
          { question_text: "Team name / number / text (leave blank if artwork only)",
            question_type: "text_input", required: false,
            instructions: "Type the exact wording as you want it printed." },
          { question_text: "Upload your design",
            question_type: "labeled_upload", required: true, max_allowed_files: 2,
            options: [{ option_id: 1, label: "FRONT" }, { option_id: 2, label: "BACK" }],
            instructions: "Upload PNG or SVG with a transparent background if possible." },
        ],
  );

  const addTag = () => {
    const raw = tagInput.trim().slice(0, 20).replace(/[^a-zA-Z0-9 \-]/g, "").trim();
    if (!raw) return;
    if (tags.length >= 13) { toast.error("Etsy allows a max of 13 tags"); return; }
    if (tags.some((t) => t.toLowerCase() === raw.toLowerCase())) return;
    setTags([...tags, raw]);
    setTagInput("");
  };
  const removeTag = (i) => setTags(tags.filter((_, idx) => idx !== i));

  const patchQ = (i, patch) => setQuestions(questions.map((q, idx) => idx === i ? { ...q, ...patch } : q));
  const removeQ = (i) => setQuestions(questions.filter((_, idx) => idx !== i));
  const addQ = (type) => {
    if (questions.length >= 5) { toast.error("Etsy allows a max of 5 questions"); return; }
    const uploads = questions.filter((q) => (q.question_type || "").endsWith("upload")).length;
    if (type.endsWith("upload") && uploads >= 1) {
      toast.error("Etsy allows only one file-upload question per listing");
      return;
    }
    const base = { question_text: "New field", question_type: type, required: false, instructions: "" };
    if (type === "dropdown") base.options = [{ option_id: 1, label: "Option 1" }, { option_id: 2, label: "Option 2" }];
    if (type === "labeled_upload") { base.max_allowed_files = 2; base.options = [{ option_id: 1, label: "FRONT" }, { option_id: 2, label: "BACK" }]; }
    if (type === "unlabeled_upload") { base.max_allowed_files = 3; }
    setQuestions([...questions, base]);
  };

  const doSave = () => {
    // Auto-sync labeled_upload options count with max_allowed_files
    const cleaned = questions.map((q) => {
      if (q.question_type === "labeled_upload") {
        const n = q.options?.length || 2;
        return { ...q, max_allowed_files: n };
      }
      return q;
    });
    onSave(product, {
      etsy_primary_image: url,
      etsy_tags: tags,
      etsy_personalization_questions: cleaned,
    });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           className="surface-card max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
           data-testid="etsy-listing-editor-modal">
        <div className="p-5 border-b border-[#222] flex justify-between items-center">
          <div>
            <div className="font-mono text-[10px] text-zinc-500">ETSY LISTING EDITOR</div>
            <h3 className="font-display text-xl uppercase mt-1">{product.name}</h3>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white text-xl">×</button>
        </div>
        <div className="border-b border-[#222] flex flex-wrap">
          <_EtsyEditorTabBtn id="image" label="1 · Primary Image" active={tab === "image"} onClick={() => setTab("image")} />
          <_EtsyEditorTabBtn id="tags" label={`2 · Tags (${tags.length}/13)`} active={tab === "tags"} onClick={() => setTab("tags")} />
          <_EtsyEditorTabBtn id="options" label={`3 · Custom Options (${questions.length}/5)`} active={tab === "options"} onClick={() => setTab("options")} />
        </div>
        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          {tab === "image" && (
            <>
              <label className="block">
                <div className="font-mono text-[10px] tracking-widest text-zinc-500 mb-1">
                  PRIMARY IMAGE URL — a finished, customized shot (mockup / real print photo)
                </div>
                <input type="text" value={url} onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://…/finished-shirt-front.jpg"
                  data-testid="etsy-primary-image-url"
                  className="w-full bg-black border border-[#222] focus:border-[var(--tc-red)] px-3 py-2 text-sm outline-none" />
              </label>
              {url && (
                <div className="border border-[#222] p-2">
                  <img src={url} alt="preview" className="w-full h-40 object-contain bg-black"
                       onError={(e) => { e.target.style.display = "none"; }} />
                </div>
              )}
            </>
          )}

          {tab === "tags" && (
            <>
              <div className="font-mono text-[10px] tracking-widest text-zinc-500 mb-2">
                ETSY TAGS · up to 13 · 20 chars each · letters, numbers, spaces & hyphens only
              </div>
              <div className="flex flex-wrap gap-2 mb-3" data-testid="etsy-tag-list">
                {tags.map((t, i) => (
                  <span key={i} className="inline-flex items-center gap-2 bg-[#111] border border-[#222] text-white px-3 py-1 text-xs font-mono">
                    {t}
                    <button onClick={() => removeTag(i)} className="text-zinc-500 hover:text-[var(--tc-red)]" data-testid={`etsy-tag-remove-${i}`}>×</button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input type="text" value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                  placeholder="e.g. custom t-shirt"
                  maxLength={20}
                  data-testid="etsy-tag-input"
                  className="flex-1 bg-black border border-[#222] focus:border-[var(--tc-red)] px-3 py-2 text-sm outline-none" />
                <button onClick={addTag} disabled={tags.length >= 13 || !tagInput.trim()}
                        data-testid="etsy-tag-add"
                        className="btn-tc-secondary text-xs disabled:opacity-40">Add tag</button>
              </div>
              <div className="text-[10px] text-zinc-500">
                Tip: front-load your most searched keywords (custom t-shirt, personalized tee, team shirt).
                Etsy weighs the first few tags heavier for search ranking.
              </div>
            </>
          )}

          {tab === "options" && (
            <>
              <div className="font-mono text-[10px] tracking-widest text-zinc-500 mb-2">
                CUSTOM OPTIONS — buyer input fields Etsy shows on the listing page
              </div>
              <div className="space-y-3" data-testid="etsy-questions-list">
                {questions.map((q, i) => (
                  <div key={i} className="border border-[#222] p-3 space-y-2" data-testid={`etsy-question-${i}`}>
                    <div className="flex items-center justify-between">
                      <div className="text-[10px] font-mono text-zinc-500 uppercase">
                        {q.question_type.replace("_", " ")}
                      </div>
                      <button onClick={() => removeQ(i)} className="text-zinc-500 hover:text-[var(--tc-red)] text-xs"
                              data-testid={`etsy-question-remove-${i}`}>Remove</button>
                    </div>
                    <input type="text" value={q.question_text}
                      onChange={(e) => patchQ(i, { question_text: e.target.value.slice(0, 45) })}
                      placeholder="What buyers see (e.g. Team name)"
                      maxLength={45}
                      data-testid={`etsy-question-text-${i}`}
                      className="w-full bg-black border border-[#222] focus:border-[var(--tc-red)] px-3 py-2 text-sm outline-none" />
                    <input type="text" value={q.instructions || ""}
                      onChange={(e) => patchQ(i, { instructions: e.target.value })}
                      placeholder="Helper text (optional)"
                      className="w-full bg-black border border-[#222] focus:border-[var(--tc-red)] px-3 py-2 text-xs outline-none text-zinc-400" />
                    {q.question_type === "dropdown" && (
                      <div className="pl-3 border-l-2 border-[#333] space-y-1">
                        {(q.options || []).map((o, j) => (
                          <div key={j} className="flex gap-2 items-center">
                            <input type="text" value={o.label}
                              onChange={(e) => patchQ(i, {
                                options: q.options.map((oo, k) => k === j ? { ...oo, label: e.target.value } : oo),
                              })}
                              className="flex-1 bg-black border border-[#222] px-2 py-1 text-xs" />
                            <button onClick={() => patchQ(i, { options: q.options.filter((_, k) => k !== j) })}
                                    className="text-zinc-500 text-xs">×</button>
                          </div>
                        ))}
                        <button onClick={() => patchQ(i, {
                          options: [...(q.options || []),
                            { option_id: (q.options?.length || 0) + 1, label: `Option ${(q.options?.length || 0) + 1}` }],
                        })} className="text-[10px] text-[var(--tc-red)] font-mono">+ Add option</button>
                      </div>
                    )}
                    {q.question_type === "labeled_upload" && (
                      <div className="pl-3 border-l-2 border-[#333] space-y-1">
                        <div className="text-[10px] font-mono text-zinc-500 uppercase">File labels · one per required upload slot</div>
                        {(q.options || []).map((o, j) => (
                          <div key={j} className="flex gap-2 items-center">
                            <input type="text" value={o.label}
                              onChange={(e) => patchQ(i, {
                                options: q.options.map((oo, k) => k === j ? { ...oo, label: e.target.value } : oo),
                              })}
                              className="flex-1 bg-black border border-[#222] px-2 py-1 text-xs" />
                            <button onClick={() => patchQ(i, {
                              options: q.options.filter((_, k) => k !== j),
                            })} className="text-zinc-500 text-xs">×</button>
                          </div>
                        ))}
                        <button disabled={(q.options?.length || 0) >= 10}
                          onClick={() => patchQ(i, {
                            options: [...(q.options || []),
                              { option_id: (q.options?.length || 0) + 1, label: `LABEL ${(q.options?.length || 0) + 1}` }],
                          })} className="text-[10px] text-[var(--tc-red)] font-mono disabled:opacity-40">+ Add slot</button>
                      </div>
                    )}
                    {q.question_type === "unlabeled_upload" && (
                      <div className="flex items-center gap-2">
                        <div className="text-[10px] font-mono text-zinc-500 uppercase">Files allowed</div>
                        <input type="number" min="1" max="10"
                          value={q.max_allowed_files || 3}
                          onChange={(e) => patchQ(i, { max_allowed_files: Math.max(1, Math.min(10, parseInt(e.target.value) || 1)) })}
                          className="w-20 bg-black border border-[#222] px-2 py-1 text-xs" />
                      </div>
                    )}
                    <label className="flex items-center gap-2 text-xs text-zinc-400">
                      <input type="checkbox" checked={!!q.required}
                             onChange={(e) => patchQ(i, { required: e.target.checked })}
                             data-testid={`etsy-question-required-${i}`} />
                      Required
                    </label>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-2 pt-2">
                <button onClick={() => addQ("text_input")} disabled={questions.length >= 5}
                        data-testid="etsy-add-text"
                        className="btn-tc-secondary text-xs disabled:opacity-40">+ Text box</button>
                <button onClick={() => addQ("dropdown")} disabled={questions.length >= 5}
                        data-testid="etsy-add-dropdown"
                        className="btn-tc-secondary text-xs disabled:opacity-40">+ Dropdown list</button>
                <button onClick={() => addQ("labeled_upload")} disabled={questions.length >= 5}
                        data-testid="etsy-add-upload"
                        className="btn-tc-secondary text-xs disabled:opacity-40">+ File upload (labeled)</button>
                <button onClick={() => addQ("unlabeled_upload")} disabled={questions.length >= 5}
                        data-testid="etsy-add-upload-unlabeled"
                        className="btn-tc-secondary text-xs disabled:opacity-40">+ File upload (single)</button>
              </div>
              <div className="text-[10px] text-zinc-500 pt-2">
                Etsy caps this at 5 questions total, with only one file-upload question per listing (max 10 files).
              </div>
            </>
          )}
        </div>
        <div className="p-5 border-t border-[#222] flex justify-between items-center">
          <div className="text-[10px] text-zinc-500 font-mono">
            Changes push to Etsy on the next Create/Update Drafts run.
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="btn-tc-secondary" data-testid="etsy-editor-cancel">Cancel</button>
            <button onClick={doSave} data-testid="etsy-editor-save" className="btn-tc-primary">Save</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ReadyMadeEtsyPanel({ pw }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [ipAudit, setIpAudit] = useState(null);        // last audit findings
  const [ipRunning, setIpRunning] = useState(false);
  const [ipUseAi, setIpUseAi] = useState(false);

  const reload = useCallback(async ({ silent = true } = {}) => {
    setLoading(true);
    try {
      const { data: d } = await apiClient.get("/admin/etsy/ready-made/preview", {
        headers: { "X-Admin-Password": pw },
      });
      setData(d);
      if (!silent) {
        const s = d?.summary || {};
        toast.success(`Ready-Made preview refreshed — ${s.ready || 0} ready, ${s.draft || 0} drafts`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally { setLoading(false); }
  }, [pw]);
  useEffect(() => { reload(); }, [reload]);

  const products = data?.products || [];
  const filtered = useMemo(() => {
    let rows = products;
    if (statusFilter !== "all") rows = rows.filter((r) => r.status === statusFilter);
    if (q) {
      const s = q.toLowerCase();
      rows = rows.filter((r) => (r.name || "").toLowerCase().includes(s)
                                 || (r.safe_title || "").toLowerCase().includes(s)
                                 || (r.category || "").toLowerCase().includes(s));
    }
    return rows;
  }, [products, statusFilter, q]);

  const readySelected = filtered.filter((p) => selected.has(p.id) && p.status !== "missing_info");
  const MAX_SELECTION = 50;   // backend enforces "Cap of 50 ready-made products per batch"
  const toggle = (id) => setSelected((prev) => {
    const n = new Set(prev);
    if (n.has(id)) { n.delete(id); return n; }
    if (n.size >= MAX_SELECTION) {
      toast.error(`Batch cap reached — ${MAX_SELECTION} products max per sync. Deselect one to add another.`,
        { duration: 4000 });
      return prev;
    }
    n.add(id); return n;
  });
  const toggleAll = () => {
    const eligible = filtered.filter((p) => p.status !== "missing_info").map((p) => p.id);
    setSelected((prev) => {
      if (eligible.every((id) => prev.has(id))) return new Set();
      if (eligible.length > MAX_SELECTION) {
        toast.error(
          `Only selecting the first ${MAX_SELECTION} of ${eligible.length} eligible products — backend caps each sync at ${MAX_SELECTION}. Run this again after they're synced to push the rest.`,
          { duration: 6000 });
        return new Set(eligible.slice(0, MAX_SELECTION));
      }
      return new Set(eligible);
    });
  };

  const runDrafts = async () => {
    if (readySelected.length === 0) { toast.error("Select at least one ready product"); return; }
    if (!window.confirm(
      `Push ${readySelected.length} ready-made design(s) to Etsy as drafts?\n\n`
      + "Titles + descriptions are auto-rewritten to Etsy-safe generic terms "
      + "(e.g. Harry Potter → Magic School). No customer personalization is added.",
    )) return;
    setBusy(true);
    const progressToast = toast.loading(`Starting sync of ${readySelected.length}…`);
    try {
      const { data: kickoff } = await apiClient.post("/admin/etsy/ready-made/drafts",
        { product_ids: readySelected.map((p) => p.id) },
        { headers: { "X-Admin-Password": pw } });
      const finalDoc = await pollEtsyJob(kickoff.job_id, pw, (d) => {
        toast.loading(`${d.message || "Syncing…"} · ${d.completed}/${d.total}`,
          { id: progressToast });
      });
      const parts = Object.entries(finalDoc.summary?.counts || finalDoc.counts || {})
        .map(([k, v]) => `${v} ${k}`).join(" · ");
      toast.success(`Ready-made sync done — ${parts}`,
        { id: progressToast, duration: 12000 });
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Sync failed",
        { id: progressToast });
    } finally { setBusy(false); }
  };

  const syncAllReady = async () => {
    // Grab every "ready" product across the WHOLE catalogue (not just
    // the current filter) so the admin can push everything in one click.
    const eligible = (data?.products || [])
      .filter((p) => p.status === "ready")
      .map((p) => p.id);
    if (eligible.length === 0) {
      toast.error("Nothing to sync — every product is already drafted, active, or missing info.");
      return;
    }
    const totalWaves = Math.ceil(eligible.length / MAX_SELECTION);
    if (!window.confirm(
      `Push ALL ${eligible.length} ready-made design(s) to Etsy?\n\n`
      + `Will run in ${totalWaves} wave(s) of up to ${MAX_SELECTION} products each. `
      + "Titles + descriptions auto-rewritten to Etsy-safe terms. "
      + "You can leave this tab open — a toast will show progress after each wave.",
    )) return;
    setBusy(true);
    const progressToast = toast.loading(`Preparing ${totalWaves} wave(s)…`);
    const aggregate = {};
    let done = 0;
    let failedWave = null;
    try {
      for (let i = 0; i < eligible.length; i += MAX_SELECTION) {
        const wave = eligible.slice(i, i + MAX_SELECTION);
        const waveNum = Math.floor(i / MAX_SELECTION) + 1;
        try {
          const { data: kickoff } = await apiClient.post(
            "/admin/etsy/ready-made/drafts",
            { product_ids: wave },
            { headers: { "X-Admin-Password": pw } },
          );
          const doc = await pollEtsyJob(kickoff.job_id, pw, (d) => {
            toast.loading(
              `Wave ${waveNum}/${totalWaves} · ${d.message || "syncing"} · ${d.completed}/${d.total}`,
              { id: progressToast });
          });
          const counts = doc.summary?.counts || doc.counts || {};
          Object.entries(counts).forEach(([k, v]) => {
            aggregate[k] = (aggregate[k] || 0) + v;
          });
          done += wave.length;
        } catch (waveErr) {
          failedWave = waveNum;
          toast.error(
            `Wave ${waveNum}/${totalWaves} failed — ${waveErr?.response?.data?.detail || waveErr.message}. Stopping.`,
            { id: progressToast, duration: 10000 });
          break;
        }
      }
      const totalParts = Object.entries(aggregate).map(([k, v]) => `${v} ${k}`).join(" · ");
      if (failedWave) {
        toast.error(`Sync-all halted at wave ${failedWave} — ${done}/${eligible.length} pushed · ${totalParts}`,
          { id: progressToast, duration: 15000 });
      } else {
        toast.success(`Sync-all complete — ${done}/${eligible.length} pushed · ${totalParts}`,
          { id: progressToast, duration: 15000 });
      }
      await reload();
    } finally { setBusy(false); }
  };

  const resyncAll = async () => {
    const total = (data?.summary?.draft || 0) + (data?.summary?.active || 0) + (data?.summary?.inactive || 0);
    if (total === 0) { toast.error("No Ready-Made listings have been synced to Etsy yet"); return; }
    if (!window.confirm(
      `Re-push ALL ${total} already-synced Ready-Made listing(s) to Etsy?\n\n`
      + "This picks up the latest Etsy Settings (markup %, rounding, "
      + "shipping profile) and re-prices every existing listing. "
      + "Listings with no changes cost zero API calls. Safe to run repeatedly.",
    )) return;
    setBusy(true);
    const t = toast.loading("Starting resync…");
    try {
      const { data: kickoff } = await apiClient.post("/admin/etsy/ready-made/resync-all",
        {}, { headers: { "X-Admin-Password": pw } });
      if (!kickoff.job_id) {
        // Empty-case short-response — no work to do.
        toast.success(kickoff.message || "Nothing to resync", { id: t });
        return;
      }
      const doc = await pollEtsyJob(kickoff.job_id, pw, (d) => {
        toast.loading(`${d.message || "Resyncing…"} · ${d.completed}/${d.total}`,
          { id: t });
      });
      const parts = Object.entries(doc.summary?.counts || doc.counts || {})
        .map(([k, v]) => `${v} ${k}`).join(" · ");
      toast.success(`Reprice complete — ${doc.completed || 0} listing(s) · ${parts}`,
        { id: t, duration: 15000 });
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Reprice failed", { id: t });
    } finally { setBusy(false); }
  };

  const runIpAudit = async () => {
    setIpRunning(true);
    try {
      const { data: r } = await apiClient.post("/admin/etsy/ready-made/ip-audit",
        { use_ai: ipUseAi }, { headers: { "X-Admin-Password": pw }, timeout: 600000 });
      setIpAudit(r);
      const c = r.counts || {};
      const risky = c.risky || 0;
      const suffix = r.ai_enabled ? " (AI on)" : "";
      if (risky === 0) toast.success(`IP audit${suffix} — all ${c.scanned || 0} listings clean`);
      else toast.error(`IP audit${suffix} — ${risky} of ${c.scanned || 0} listings are RISKY`, { duration: 8000 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "IP audit failed");
    } finally { setIpRunning(false); }
  };

  // Batched AI audit — pulls the target list first, then scans in
  // AI_BATCH_SIZE-per-wave chunks with per-wave progress toasts.
  // Critical for shops with 100+ listings where a single AI-enabled
  // scan would time out. Cache-hits on unchanged listings make the
  // second run near-instant (see etsy_ip_ai._content_hash).
  const AI_BATCH_SIZE = 15;
  const runIpAuditBatched = async () => {
    setIpRunning(true);
    const merged = { counts: { scanned: 0, clean: 0, risky: 0, error: 0, missing_product: 0 }, findings: [], ai_enabled: ipUseAi };
    try {
      const { data: t } = await apiClient.get(
        "/admin/etsy/ready-made/ip-audit/targets",
        { headers: { "X-Admin-Password": pw } });
      const ids = t.listing_ids || [];
      if (ids.length === 0) { toast.error("Nothing to audit — no synced Ready-Made listings"); return; }
      const totalWaves = Math.ceil(ids.length / AI_BATCH_SIZE);
      toast.success(
        `Batched audit — ${ids.length} listing(s) across ${totalWaves} wave(s) of ${AI_BATCH_SIZE}${ipUseAi ? " (AI on)" : ""}`,
        { duration: 4000 });

      for (let i = 0; i < ids.length; i += AI_BATCH_SIZE) {
        const wave = ids.slice(i, i + AI_BATCH_SIZE);
        const waveNum = Math.floor(i / AI_BATCH_SIZE) + 1;
        try {
          const { data: r } = await apiClient.post(
            "/admin/etsy/ready-made/ip-audit",
            { use_ai: ipUseAi, listing_ids: wave },
            { headers: { "X-Admin-Password": pw }, timeout: 300000 });
          Object.entries(r.counts || {}).forEach(([k, v]) => {
            merged.counts[k] = (merged.counts[k] || 0) + v;
          });
          merged.findings.push(...(r.findings || []));
          setIpAudit({ ...merged });   // stream — user sees findings appear per wave
          toast.success(
            `Wave ${waveNum}/${totalWaves} · scanned ${r.counts?.scanned || wave.length} · ${r.counts?.risky || 0} risky`,
            { duration: 3500 });
        } catch (waveErr) {
          toast.error(
            `Wave ${waveNum}/${totalWaves} failed — ${waveErr?.response?.data?.detail || waveErr.message}. Continuing…`,
            { duration: 5000 });
        }
      }

      const c = merged.counts;
      if ((c.risky || 0) === 0)
        toast.success(`Batched audit complete — all ${c.scanned} listings clean`);
      else
        toast.error(`Batched audit complete — ${c.risky} of ${c.scanned} listings RISKY`,
          { duration: 12000 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Batched audit failed");
    } finally { setIpRunning(false); }
  };

  const runIpFix = async (listingIds = null) => {
    const target = Array.isArray(listingIds) ? listingIds.length : (ipAudit?.counts?.risky || 0);
    if (!target) { toast.error("Nothing to fix — run an audit first"); return; }
    if (!window.confirm(
      `PATCH ${target} Etsy listing(s) with the IP-safe title, description, and tags?\n\n`
      + "This rewrites the LIVE listings on Etsy immediately. Cannot be un-done "
      + "automatically (you'd need to sync the original product again).",
    )) return;
    setIpRunning(true);
    const t = toast.loading(`Starting IP fix of ${target} listing(s)…`);
    try {
      const body = { use_ai: ipUseAi };
      if (Array.isArray(listingIds)) body.listing_ids = listingIds;
      const { data: kickoff } = await apiClient.post("/admin/etsy/ready-made/ip-fix",
        body, { headers: { "X-Admin-Password": pw } });
      const doc = await pollEtsyJob(kickoff.job_id, pw, (d) => {
        toast.loading(`${d.message || "Fixing…"} · ${d.completed}/${d.total}`,
          { id: t });
      });
      const parts = Object.entries(doc.summary?.counts || doc.counts || {})
        .map(([k, v]) => `${v} ${k}`).join(" · ");
      toast.success(`IP fix — ${doc.completed || 0} listing(s) patched · ${parts}`,
        { id: t, duration: 12000 });
      await runIpAudit();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "IP fix failed", { id: t });
    } finally { setIpRunning(false); }
  };

  const unlinkRemoved = async () => {
    const removedCount = data?.summary?.removed || 0;
    if (removedCount === 0) { toast.error("No removed mappings to unlink"); return; }
    if (!window.confirm(
      `Unlink ${removedCount} Ready-Made mapping(s) whose Etsy listings were deleted?\n\n`
      + "These products will fall back to \"ready\" status so you can push them as fresh Etsy drafts. "
      + "This does NOT affect your storefront — mappings are the internal ↔ Etsy link only.",
    )) return;
    setBusy(true);
    try {
      const { data: r } = await apiClient.post("/admin/etsy/ready-made/unlink-removed",
        {}, { headers: { "X-Admin-Password": pw } });
      toast.success(`Unlinked ${r.unlinked || 0} stale mapping(s)`);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unlink failed");
    } finally { setBusy(false); }
  };

  if (loading && !data) return (
    <div className="surface-card p-6 text-zinc-500 text-sm font-mono">Loading Ready-Made preview…</div>
  );
  const s = data?.summary || {};

  return (
    <div className="surface-card p-6" data-testid="etsy-ready-made-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="font-mono text-[10px] tracking-widest text-zinc-500 flex items-center gap-2">
          <ImageIcon size={13} /> READY-MADE DESIGNS · Phase 6 · IP-safe auto-rewrite enabled
        </div>
        <div className="flex gap-2 flex-wrap">
          <button data-testid="etsy-rm-drafts-btn" onClick={runDrafts}
            disabled={busy || readySelected.length === 0}
            title={`Batch cap: ${MAX_SELECTION} per sync (Etsy rate-limit safe)`}
            className="btn-tc-primary text-xs flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
            <Upload size={13} />{busy ? "Syncing…" : `Create/Update Drafts (${readySelected.length}/${MAX_SELECTION})`}
          </button>
          {(s.ready || 0) > 0 && (
            <button data-testid="etsy-rm-sync-all-ready-btn" onClick={syncAllReady}
              disabled={busy}
              title={`Auto-chunk all ${s.ready} ready product(s) into ${Math.ceil((s.ready || 0) / MAX_SELECTION)} wave(s) of ${MAX_SELECTION}`}
              className="btn-tc-primary text-xs flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed border-emerald-500 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20">
              <Rocket size={13} />{busy ? "Working…" : `Sync All Ready (${s.ready} · ${Math.ceil((s.ready || 0) / MAX_SELECTION)} waves)`}
            </button>
          )}
          <button data-testid="etsy-rm-resync-all-btn" onClick={resyncAll}
            disabled={busy}
            title="Re-push every synced listing so Etsy picks up the latest markup, rounding, and shipping settings."
            className="btn-tc-secondary text-xs flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
            <RefreshCw size={13} />{busy ? "Working…" : "Reprice All Synced"}
          </button>
          {(data?.summary?.removed || 0) > 0 && (
            <button data-testid="etsy-rm-unlink-removed-btn" onClick={unlinkRemoved}
              disabled={busy}
              title="Delete mappings for listings the shop already removed on Etsy so they can be re-drafted fresh."
              className="btn-tc-secondary text-xs flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed border-[var(--tc-red)] text-[var(--tc-red)]">
              Unlink Removed ({data.summary.removed})
            </button>
          )}
          <button data-testid="etsy-rm-refresh" onClick={() => reload({ silent: false })}
            className="btn-tc-secondary text-[10px] flex items-center gap-1">
            <RefreshCw size={11} /> Refresh
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 mb-4">
        {[["total","Total"],["ready","Ready"],["missing_info","Missing"],["draft","Draft"],["active","Active"],["removed","Removed"]].map(([k,label]) => (
          <button key={k} onClick={() => setStatusFilter(statusFilter === k ? "all" : k)}
            className={`border p-2 text-left transition ${statusFilter === k ? "border-[var(--tc-red)]" : "border-[#222] hover:border-zinc-600"}`}>
            <div className="font-mono text-[9px] tracking-widest text-zinc-500 uppercase">{label}</div>
            <div className="text-white font-display text-xl">{s[k] ?? 0}</div>
          </button>
        ))}
      </div>

      {/* IP Audit — scans live Etsy listings for brand-risky terms and rewrites them safely */}
      <div className="border border-[#222] p-3 mb-4" data-testid="etsy-rm-ip-audit-panel">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          <div className="font-mono text-[10px] tracking-widest text-zinc-500 flex items-center gap-2">
            <ShieldAlert size={13} className="text-yellow-400" /> IP AUDIT · scan live Etsy listings for takedown-risk terms
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <label className="flex items-center gap-1.5 text-[10px] font-mono text-zinc-400 cursor-pointer">
              <input type="checkbox" checked={ipUseAi} onChange={(e) => setIpUseAi(e.target.checked)}
                data-testid="etsy-rm-ip-ai-toggle" />
              <Wand2 size={11} className="text-purple-400" />
              AI scan (Claude) · catches subtle references
            </label>
            <button data-testid="etsy-rm-ip-audit-btn" onClick={runIpAudit}
              disabled={ipRunning}
              className="btn-tc-secondary text-xs flex items-center gap-2 disabled:opacity-40">
              <Search size={12} />{ipRunning ? "Scanning…" : "Run IP Audit"}
            </button>
            <button data-testid="etsy-rm-ip-audit-batched-btn" onClick={runIpAuditBatched}
              disabled={ipRunning}
              title="Chunks the whole catalogue into 15-per-wave scans with per-wave progress. Recommended when AI is on for 30+ listings."
              className="btn-tc-secondary text-xs flex items-center gap-2 disabled:opacity-40 border-cyan-500/50 text-cyan-300 hover:bg-cyan-500/10">
              <Rocket size={12} />{ipRunning ? "Scanning…" : "Batched Audit (15/wave)"}
            </button>
            {(ipAudit?.counts?.risky || 0) > 0 && (
              <button data-testid="etsy-rm-ip-fix-all-btn" onClick={() => runIpFix(null)}
                disabled={ipRunning}
                className="btn-tc-primary text-xs flex items-center gap-2 disabled:opacity-40">
                <Wand2 size={12} /> Auto-Fix All Risky ({ipAudit.counts.risky})
              </button>
            )}
          </div>
        </div>
        {ipAudit && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-2">
              {[
                ["scanned","Scanned","text-zinc-300"],
                ["clean","Clean","text-green-400"],
                ["risky","Risky","text-[var(--tc-red)]"],
                ["error","Errored","text-yellow-400"],
                ["missing_product","No Product","text-zinc-500"],
              ].map(([k,label,cls]) => (
                <div key={k} className="border border-[#222] p-2">
                  <div className="font-mono text-[9px] tracking-widest text-zinc-500 uppercase">{label}</div>
                  <div className={`font-display text-lg ${cls}`}>{ipAudit.counts?.[k] ?? 0}</div>
                </div>
              ))}
            </div>
            {(ipAudit.findings || []).filter(f => f.status === "risky").length > 0 && (
              <div className="overflow-x-auto max-h-[280px] overflow-y-auto border border-[#111]">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-black border-b border-[#222]">
                    <tr className="text-[9px] font-mono tracking-widest text-zinc-500 uppercase">
                      <th className="p-2 text-left">Etsy Listing</th>
                      <th className="p-2 text-left">Live title (risky)</th>
                      <th className="p-2 text-left">Terms found</th>
                      <th className="p-2 text-left">Safe rewrite</th>
                      <th className="p-2 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(ipAudit.findings || []).filter(f => f.status === "risky").map((f) => (
                      <tr key={f.listing_id} className="border-b border-[#111] hover:bg-[#0a0a0a]"
                        data-testid={`etsy-rm-ip-row-${f.listing_id}`}>
                        <td className="p-2">
                          <a href={f.etsy_url} target="_blank" rel="noreferrer"
                            className="text-zinc-400 hover:text-[var(--tc-red)] font-mono text-[10px] flex items-center gap-1">
                            {f.listing_id} <ExternalLink size={10} />
                          </a>
                          {(f.detected_by || []).length > 0 && (
                            <div className="flex gap-1 mt-1">
                              {(f.detected_by || []).map((d) => (
                                <span key={d} className={`text-[8px] font-mono px-1 py-0.5 border ${d === "ai" ? "text-purple-400 border-purple-500/40" : "text-yellow-400 border-yellow-500/40"}`}>
                                  {d}
                                </span>
                              ))}
                              {f.ai?.cached && (
                                <span title="AI verdict served from cache — zero LLM tokens"
                                  className="text-[8px] font-mono px-1 py-0.5 border text-cyan-400 border-cyan-500/40">cached</span>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="p-2 text-white text-[11px] max-w-[280px]">
                          <div className="truncate" title={f.live_title}>{f.live_title}</div>
                        </td>
                        <td className="p-2 text-[10px]">
                          <div className="flex flex-wrap gap-1 max-w-[240px]">
                            {[...(f.risky_terms_in_title || []), ...(f.risky_terms_in_description || []), ...(f.risky_tags || [])]
                              .slice(0, 3).map((t, i) => (
                                <span key={`r-${i}`} className="bg-[var(--tc-red)]/10 text-[var(--tc-red)] px-1.5 py-0.5 border border-[var(--tc-red)]/30 font-mono text-[9px]">
                                  {t}
                                </span>
                              ))}
                            {(f.ai?.matches || []).slice(0, 2).map((m, i) => (
                              <span key={`ai-${i}`}
                                title={m.explanation || ""}
                                className="bg-purple-500/10 text-purple-300 px-1.5 py-0.5 border border-purple-500/30 font-mono text-[9px]">
                                {m.references || m.term}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="p-2 text-green-400 text-[11px] max-w-[240px]">
                          <div className="truncate" title={f.safe_title}>{f.safe_title}</div>
                        </td>
                        <td className="p-2 text-right">
                          <button data-testid={`etsy-rm-ip-fix-${f.listing_id}`}
                            onClick={() => runIpFix([f.listing_id])}
                            disabled={ipRunning}
                            className="btn-tc-secondary text-[10px] px-2 py-1 disabled:opacity-40">
                            Fix
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {(ipAudit.counts?.risky || 0) === 0 && (
              <div className="text-xs text-green-400 flex items-center gap-2 font-mono">
                <ShieldCheck size={13} /> All {ipAudit.counts?.scanned || 0} synced listings clean.
              </div>
            )}
          </>
        )}
      </div>
      <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
        placeholder="Filter by name / slug / category…" data-testid="etsy-rm-search"
        className="w-full bg-black border border-[#222] focus:border-[var(--tc-red)] px-3 py-2 text-sm outline-none mb-3" />
      <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-black border-b border-[#222]">
            <tr className="text-[9px] font-mono tracking-widest text-zinc-500 uppercase">
              <th className="p-2 w-8"><input type="checkbox"
                checked={filtered.filter((p) => p.status !== "missing_info").every((p) => selected.has(p.id)) && filtered.length > 0}
                onChange={toggleAll} data-testid="etsy-rm-select-all" /></th>
              <th className="p-2 text-left">Product</th>
              <th className="p-2 text-left">IP-safe title</th>
              <th className="p-2 text-left">Brand</th>
              <th className="p-2 text-right">Tee/SW/Hoodie</th>
              <th className="p-2 text-center">Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => (
              <tr key={p.id} className="border-b border-[#111] hover:bg-[#0a0a0a]" data-testid={`etsy-rm-row-${p.id}`}>
                <td className="p-2">
                  <input type="checkbox" checked={selected.has(p.id)}
                    disabled={p.status === "missing_info"}
                    onChange={() => toggle(p.id)} data-testid={`etsy-rm-select-${p.id}`} />
                </td>
                <td className="p-2">
                  <div className="flex items-center gap-2">
                    {p.image && <img src={p.image} alt="" className="w-10 h-10 object-cover" />}
                    <div>
                      <div className="text-white truncate max-w-[280px]" title={p.name}>{p.name}</div>
                      <div className="text-zinc-500 text-[10px] font-mono">{p.category}</div>
                    </div>
                  </div>
                </td>
                <td className="p-2 text-white text-[11px] max-w-[240px]" title={p.safe_title}>
                  <div className="truncate">{p.safe_title}</div>
                </td>
                <td className="p-2 text-[10px] font-mono">
                  {p.brand_detected ? (
                    <span className="inline-block bg-yellow-500/10 text-yellow-300 px-2 py-0.5 border border-yellow-500/30">
                      {p.brand_detected} → safe
                    </span>
                  ) : <span className="text-zinc-600">—</span>}
                </td>
                <td className="p-2 text-right font-mono text-white text-[11px]">
                  ${p.prices?.tshirt?.toFixed(2)} / ${p.prices?.sweatshirt?.toFixed(2)} / ${p.prices?.hoodie?.toFixed(2)}
                </td>
                <td className="p-2 text-center">
                  <span className={`inline-block px-2 py-0.5 border text-[10px] font-mono uppercase ${
                    p.status === "ready" ? "border-emerald-500/40 text-emerald-300" :
                    p.status === "draft" ? "border-blue-500/40 text-blue-300" :
                    p.status === "active" ? "border-green-500/40 text-green-300" :
                    "border-amber-500/40 text-amber-300"
                  }`}>
                    {p.status}
                  </span>
                  {p.warnings?.length > 0 && (
                    <div className="text-[10px] text-amber-500 mt-1">{p.warnings[0]}</div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-[10px] text-zinc-500 leading-relaxed pt-3">
        <strong className="text-yellow-400">IP-safe auto-rewrite:</strong> branded titles are automatically converted to
        generic Etsy-searchable terms (Harry Potter → Magic School, Marvel → Comic Book Hero, DBZ → Anime Warrior…).
        Descriptions and tags follow the same rewrite. Images are pushed as-is — Etsy may still remove
        listings whose artwork infringes on a copyrighted character, so review before publishing.
      </div>
    </div>
  );
}


function EtsyOrdersPanel({ pw }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/admin/etsy/orders/status", {
        headers: { "X-Admin-Password": pw },
      });
      setStatus(data);
    } catch (_e) { /* silent — secondary panel */ }
  }, [pw]);
  useEffect(() => { reload(); }, [reload]);

  const syncNow = async () => {
    setBusy(true);
    try {
      const { data: r } = await apiClient.post("/admin/etsy/orders/sync-now", {},
        { headers: { "X-Admin-Password": pw } });
      if (r.ok) {
        const parts = [
          `Etsy returned ${r.receipts_fetched ?? "?"} receipt(s)`,
          `imported ${r.imported}`,
          `already-had ${r.skipped}`,
        ];
        if (r.email_failed) parts.push(`EMAIL FAILED for ${r.email_failed}`);
        toast.success(parts.join(" · "), { duration: 10000 });
        if (r.latest_receipt_id) {
          toast.info(`Latest receipt: ${r.latest_receipt_id} · watermark now ${r.watermark_iso}`,
                      { duration: 10000 });
        }
      } else {
        toast.error(r.error || "Etsy orders sync failed");
      }
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally { setBusy(false); }
  };

  const lastAt = status?.last_run_at ? new Date(status.last_run_at).toLocaleString() : "never";
  const lastCount = status?.last_run_count ?? 0;
  const lastErr = status?.last_run_error;

  return (
    <div className="surface-card p-6" data-testid="etsy-orders-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="font-mono text-[10px] tracking-widest text-zinc-500 flex items-center gap-2">
          <ShoppingBag size={13} /> ETSY ORDER IMPORT · Phase 5
        </div>
        <button
          data-testid="etsy-orders-sync-now-btn"
          onClick={syncNow}
          disabled={busy}
          className="btn-tc-primary text-xs flex items-center gap-2 disabled:opacity-50"
        >
          <RefreshCw size={11} /> {busy ? "Syncing…" : "Sync Etsy Orders now"}
        </button>
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        <div className="border border-[#222] p-3">
          <div className="font-mono text-[9px] tracking-widest text-zinc-500 uppercase">Last run</div>
          <div className="text-white font-mono text-sm mt-1" data-testid="etsy-orders-last-run">{lastAt}</div>
        </div>
        <div className="border border-[#222] p-3">
          <div className="font-mono text-[9px] tracking-widest text-zinc-500 uppercase">Imported (last run)</div>
          <div className="text-white font-display text-2xl mt-1" data-testid="etsy-orders-last-count">{lastCount}</div>
        </div>
        <div className={`border p-3 ${lastErr ? "border-red-500/40 bg-red-500/5" : "border-[#222]"}`}>
          <div className="font-mono text-[9px] tracking-widest text-zinc-500 uppercase">Status</div>
          <div className={`text-sm mt-1 font-mono ${lastErr ? "text-red-300" : "text-emerald-300"}`}
               data-testid="etsy-orders-last-status">
            {lastErr || "healthy — auto-polls every 5 min"}
          </div>
        </div>
      </div>
      <p className="text-[10px] text-zinc-500 leading-relaxed mt-3">
        Etsy has no push webhooks — we poll `getShopReceipts` on a 5-min interval.
        New paid receipts are added to the Orders admin (channel <span className="text-white font-mono">etsy</span>)
        and you&apos;ll get a <span className="text-white">🛍 New Etsy order</span> email for each one.
      </p>
    </div>
  );
}


function SyncLogPanel({ pw }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/admin/etsy/sync-log", {
        headers: { "X-Admin-Password": pw },
      });
      setRows(data.rows || []);
    } catch (e) {
      // Silent — sync log is a secondary panel.
    } finally { setLoading(false); }
  }, [pw]);
  useEffect(() => { reload(); }, [reload]);

  const retry = async (productId) => {
    try {
      await apiClient.post(`/admin/etsy/sync-log/${productId}/retry`, {},
        { headers: { "X-Admin-Password": pw } });
      toast.success("Retry queued");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry failed");
    }
  };

  return (
    <div className="surface-card p-6" data-testid="etsy-sync-log-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="font-mono text-[10px] tracking-widest text-zinc-500 flex items-center gap-2">
          <ListChecks size={13} /> SYNC LOG · Last 50
        </div>
        <button onClick={reload} className="btn-tc-secondary text-[10px] flex items-center gap-1">
          <RefreshCw size={11} /> Refresh
        </button>
      </div>
      {loading ? (
        <div className="text-zinc-500 font-mono text-sm py-4">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-zinc-500 font-mono text-sm py-4 text-center">
          No sync events yet. Run Create/Update Drafts above to populate.
        </div>
      ) : (
        <div className="overflow-x-auto border border-[#222] max-h-96 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#050505] border-b border-[#222] sticky top-0">
              <tr className="font-mono text-[10px] tracking-widest text-zinc-500">
                <th className="p-2 text-left">WHEN</th>
                <th className="p-2 text-left">ACTION</th>
                <th className="p-2 text-left">STATUS</th>
                <th className="p-2 text-left">MESSAGE</th>
                <th className="p-2 text-left w-16">RETRY</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-[#222]" data-testid={`etsy-log-${r.id}`}>
                  <td className="p-2 font-mono text-[10px] text-zinc-400 whitespace-nowrap">
                    {r.created_at?.slice(11, 19)} · {r.created_at?.slice(0, 10)}
                  </td>
                  <td className="p-2 font-mono text-[10px] uppercase">{r.action}</td>
                  <td className="p-2"><StatusBadge status={r.status === "ok" ? "active"
                                                          : r.status === "skipped" ? "not_connected"
                                                          : r.status === "missing_info" ? "missing_info"
                                                          : "sync_error"} /></td>
                  <td className="p-2 text-[11px] text-zinc-300 max-w-md truncate" title={r.message}>{r.message}</td>
                  <td className="p-2">
                    {r.status === "error" && r.internal_product_id && (
                      <button onClick={() => retry(r.internal_product_id)}
                              data-testid={`etsy-log-retry-${r.internal_product_id}`}
                              className="text-amber-400 hover:text-white text-[10px] font-mono uppercase">
                        Retry
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const TONE_CLASSES = {
  emerald: "bg-emerald-500/10 border-emerald-500 text-emerald-300",
  amber:   "bg-amber-500/10 border-amber-500 text-amber-300",
  blue:    "bg-blue-500/10 border-blue-500 text-blue-300",
  red:     "bg-red-500/10 border-red-500 text-red-300",
  zinc:    "bg-zinc-500/10 border-zinc-500 text-zinc-300",
};

function SummaryChip({ label, value, tone = "zinc", active, onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={`etsy-summary-${label.toLowerCase().replace(" ", "-")}`}
      className={`border px-3 py-2 text-left transition-colors ${
        active ? "border-[var(--tc-red)] bg-[var(--tc-red)]/10"
               : "border-[#222] hover:border-zinc-600"
      }`}
    >
      <div className={`font-mono text-[9px] tracking-widest uppercase ${TONE_CLASSES[tone].split(" ").find((c) => c.startsWith("text-"))}`}>
        {label}
      </div>
      <div className="font-display text-xl font-bold text-white">{value}</div>
    </button>
  );
}

function StatusBadge({ status }) {
  const map = {
    ready:        { label: "Ready",       tone: "emerald" },
    missing_info: { label: "Missing info", tone: "amber" },
    draft:        { label: "Draft",       tone: "blue" },
    active:       { label: "Active",      tone: "emerald" },
    sync_error:   { label: "Sync error",  tone: "red" },
    not_connected: { label: "Not connected", tone: "zinc" },
  };
  const cfg = map[status] || { label: status, tone: "zinc" };
  return (
    <span className={`inline-block border px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest ${TONE_CLASSES[cfg.tone]}`}
          data-testid={`etsy-status-badge-${status}`}>
      {cfg.label}
    </span>
  );
}


// ============================================================================
// Field helpers
// ============================================================================
function ConnectionState({ icon, label, detail }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <span className="font-ui text-base font-bold" data-testid="etsy-connection-label">{label}</span>
      </div>
      <div className="text-[12px] text-zinc-500 leading-relaxed">{detail}</div>
    </div>
  );
}

function NumberField({ label, hint, value, onChange, testId, min, max }) {
  return (
    <label className="block">
      <div className="font-mono text-[10px] tracking-widest text-zinc-500 mb-1">{label.toUpperCase()}</div>
      <input
        type="number"
        value={value ?? ""}
        min={min} max={max}
        onChange={(e) => onChange(Number(e.target.value))}
        data-testid={testId}
        className="w-full bg-black border border-[#222] focus:border-[var(--tc-red)] px-3 py-2 text-sm outline-none [color-scheme:dark]"
      />
      {hint && <div className="text-[10px] text-zinc-500 mt-1 leading-relaxed">{hint}</div>}
    </label>
  );
}

function TextField({ label, hint, value, onChange, testId }) {
  return (
    <label className="block">
      <div className="font-mono text-[10px] tracking-widest text-zinc-500 mb-1">{label.toUpperCase()}</div>
      <input
        type="text"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full bg-black border border-[#222] focus:border-[var(--tc-red)] px-3 py-2 text-sm outline-none"
      />
      {hint && <div className="text-[10px] text-zinc-500 mt-1 leading-relaxed">{hint}</div>}
    </label>
  );
}

function SelectField({ label, value, onChange, testId, options }) {
  return (
    <label className="block">
      <div className="font-mono text-[10px] tracking-widest text-zinc-500 mb-1">{label.toUpperCase()}</div>
      <select
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full bg-black border border-[#222] focus:border-[var(--tc-red)] px-3 py-2 text-sm outline-none"
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

/**
 * Dropdown when the shop's profile list is available (post-connect),
 * plain text input otherwise. Includes a "— none —" first option and
 * a small "N found" counter so the admin can tell live data is being
 * pulled from Etsy instead of guessed.
 */
function ProfileField({ label, hint, value, onChange, testId, options }) {
  const hasOptions = Array.isArray(options);
  return (
    <label className="block">
      <div className="font-mono text-[10px] tracking-widest text-zinc-500 mb-1 flex items-center justify-between">
        <span>{label.toUpperCase()}</span>
        {hasOptions && <span className="text-emerald-400 normal-case tracking-normal">{options.length} found · live</span>}
      </div>
      {hasOptions ? (
        <select
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
          className="w-full bg-black border border-[#222] focus:border-[var(--tc-red)] px-3 py-2 text-sm outline-none"
        >
          <option value="">— none —</option>
          {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
        </select>
      ) : (
        <input
          type="text"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
          placeholder="Connect Etsy first to see live options"
          className="w-full bg-black border border-[#222] focus:border-[var(--tc-red)] px-3 py-2 text-sm outline-none"
        />
      )}
      {hint && <div className="text-[10px] text-zinc-500 mt-1 leading-relaxed">{hint}</div>}
    </label>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Custom IP Audit Panel
// Scans already-synced Custom-product listings on Etsy for supplier
// brands (ATC, Gildan, SanMar, Jerzees, Hanes, Bella+Canvas, …) plus
// the full shared brand dictionary, and optionally layers Claude on
// top to catch subtle references the regex misses.
// ─────────────────────────────────────────────────────────────────────
function CustomIpAuditPanel({ pw }) {
  const [audit, setAudit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [useAi, setUseAi] = useState(false);

  const runAudit = async () => {
    setBusy(true);
    try {
      const { data: r } = await apiClient.post("/admin/etsy/custom/ip-audit",
        { use_ai: useAi }, { headers: { "X-Admin-Password": pw }, timeout: 600000 });
      setAudit(r);
      const c = r.counts || {};
      const risky = c.risky || 0;
      const suffix = r.ai_enabled ? " (AI on)" : "";
      if ((c.scanned || 0) === 0)
        toast.success(`No custom-product listings synced yet — nothing to audit`);
      else if (risky === 0)
        toast.success(`Custom IP audit${suffix} — all ${c.scanned} listings clean`);
      else
        toast.error(`Custom IP audit${suffix} — ${risky} of ${c.scanned} are RISKY`,
          { duration: 8000 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Custom IP audit failed");
    } finally { setBusy(false); }
  };

  const AI_BATCH_SIZE = 15;
  const runAuditBatched = async () => {
    setBusy(true);
    const merged = { counts: { scanned: 0, clean: 0, risky: 0, error: 0, removed: 0, missing_product: 0 }, findings: [], ai_enabled: useAi };
    try {
      const { data: t } = await apiClient.get(
        "/admin/etsy/custom/ip-audit/targets",
        { headers: { "X-Admin-Password": pw } });
      const ids = t.listing_ids || [];
      if (ids.length === 0) { toast.error("Nothing to audit — no synced Custom listings"); return; }
      const totalWaves = Math.ceil(ids.length / AI_BATCH_SIZE);
      toast.success(
        `Batched Custom audit — ${ids.length} listing(s) across ${totalWaves} wave(s)${useAi ? " (AI on)" : ""}`,
        { duration: 4000 });

      for (let i = 0; i < ids.length; i += AI_BATCH_SIZE) {
        const wave = ids.slice(i, i + AI_BATCH_SIZE);
        const waveNum = Math.floor(i / AI_BATCH_SIZE) + 1;
        try {
          const { data: r } = await apiClient.post(
            "/admin/etsy/custom/ip-audit",
            { use_ai: useAi, listing_ids: wave },
            { headers: { "X-Admin-Password": pw }, timeout: 300000 });
          Object.entries(r.counts || {}).forEach(([k, v]) => {
            merged.counts[k] = (merged.counts[k] || 0) + v;
          });
          merged.findings.push(...(r.findings || []));
          setAudit({ ...merged });
          toast.success(
            `Wave ${waveNum}/${totalWaves} · scanned ${r.counts?.scanned || wave.length} · ${r.counts?.risky || 0} risky`,
            { duration: 3500 });
        } catch (waveErr) {
          toast.error(
            `Wave ${waveNum}/${totalWaves} failed — ${waveErr?.response?.data?.detail || waveErr.message}. Continuing…`,
            { duration: 5000 });
        }
      }
      const c = merged.counts;
      if ((c.risky || 0) === 0)
        toast.success(`Batched Custom audit — all ${c.scanned} clean`);
      else
        toast.error(`Batched Custom audit — ${c.risky} of ${c.scanned} RISKY`,
          { duration: 12000 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Batched Custom audit failed");
    } finally { setBusy(false); }
  };

  const runFix = async (listingIds = null) => {
    const target = Array.isArray(listingIds) ? listingIds.length : (audit?.counts?.risky || 0);
    if (!target) { toast.error("Nothing to fix — run an audit first"); return; }
    if (!window.confirm(
      `PATCH ${target} Custom Etsy listing(s) with the IP-safe title + description?\n\n`
      + "Live-rewrites titles/descriptions on Etsy immediately.",
    )) return;
    setBusy(true);
    const t = toast.loading(`Starting Custom IP fix of ${target}…`);
    try {
      const body = { use_ai: useAi };
      if (Array.isArray(listingIds)) body.listing_ids = listingIds;
      const { data: kickoff } = await apiClient.post("/admin/etsy/custom/ip-fix",
        body, { headers: { "X-Admin-Password": pw } });
      const doc = await pollEtsyJob(kickoff.job_id, pw, (d) => {
        toast.loading(`${d.message || "Fixing…"} · ${d.completed}/${d.total}`,
          { id: t });
      });
      const parts = Object.entries(doc.summary?.counts || doc.counts || {})
        .map(([k, v]) => `${v} ${k}`).join(" · ");
      toast.success(`Custom IP fix — ${doc.completed || 0} patched · ${parts}`,
        { id: t, duration: 10000 });
      await runAudit();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Custom IP fix failed", { id: t });
    } finally { setBusy(false); }
  };

  return (
    <div className="surface-card p-6" data-testid="etsy-custom-ip-panel">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div className="font-mono text-[10px] tracking-widest text-zinc-500 flex items-center gap-2">
          <ShieldAlert size={13} className="text-yellow-400" /> CUSTOM PRODUCTS · IP AUDIT · supplier brand + character detection
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="flex items-center gap-1.5 text-[10px] font-mono text-zinc-400 cursor-pointer">
            <input type="checkbox" checked={useAi} onChange={(e) => setUseAi(e.target.checked)}
              data-testid="etsy-custom-ip-ai-toggle" />
            <Wand2 size={11} className="text-purple-400" /> AI scan (Claude)
          </label>
          <button data-testid="etsy-custom-ip-audit-btn" onClick={runAudit} disabled={busy}
            className="btn-tc-secondary text-xs flex items-center gap-2 disabled:opacity-40">
            <Search size={12} />{busy ? "Scanning…" : "Run Audit"}
          </button>
          <button data-testid="etsy-custom-ip-audit-batched-btn" onClick={runAuditBatched}
            disabled={busy}
            title="Chunks the whole catalogue into 15-per-wave scans with per-wave progress."
            className="btn-tc-secondary text-xs flex items-center gap-2 disabled:opacity-40 border-cyan-500/50 text-cyan-300 hover:bg-cyan-500/10">
            <Rocket size={12} />{busy ? "Scanning…" : "Batched (15/wave)"}
          </button>
          {(audit?.counts?.risky || 0) > 0 && (
            <button data-testid="etsy-custom-ip-fix-all-btn" onClick={() => runFix(null)}
              disabled={busy}
              className="btn-tc-primary text-xs flex items-center gap-2 disabled:opacity-40">
              <Wand2 size={12} /> Auto-Fix All ({audit.counts.risky})
            </button>
          )}
        </div>
      </div>

      {!audit && (
        <div className="text-xs text-zinc-500 font-mono">
          Scans every already-synced Custom-product listing on Etsy. Enable AI to
          catch subtle references like &quot;Kamp Krystal Lake&quot; (Friday the 13th)
          or supplier brand mentions (ATC, Gildan, SanMar) buried in descriptions.
        </div>
      )}

      {audit && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 mb-3">
            {[
              ["scanned","Scanned","text-zinc-300"],
              ["clean","Clean","text-green-400"],
              ["risky","Risky","text-[var(--tc-red)]"],
              ["removed","Removed","text-zinc-500"],
              ["error","Errored","text-yellow-400"],
              ["missing_product","No Product","text-zinc-500"],
            ].map(([k,label,cls]) => (
              <div key={k} className="border border-[#222] p-2">
                <div className="font-mono text-[9px] tracking-widest text-zinc-500 uppercase">{label}</div>
                <div className={`font-display text-lg ${cls}`}>{audit.counts?.[k] ?? 0}</div>
              </div>
            ))}
          </div>
          {(audit.counts?.removed || 0) > 0 && (
            <button data-testid="etsy-custom-ip-unlink-removed-btn"
              onClick={async () => {
                if (!window.confirm(`Unlink ${audit.counts.removed} Custom mapping(s) whose Etsy listings were deleted?`)) return;
                setBusy(true);
                try {
                  const { data: r } = await apiClient.post(
                    "/admin/etsy/custom/unlink-removed", {},
                    { headers: { "X-Admin-Password": pw } });
                  toast.success(`Unlinked ${r.unlinked || 0} stale mapping(s)`);
                  await runAudit();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Unlink failed");
                } finally { setBusy(false); }
              }}
              disabled={busy}
              className="btn-tc-secondary text-xs mb-2 border-[var(--tc-red)] text-[var(--tc-red)]">
              Unlink Removed ({audit.counts.removed})
            </button>
          )}
          {(audit.findings || []).filter(f => f.status === "risky").length > 0 ? (
            <div className="overflow-x-auto max-h-[320px] overflow-y-auto border border-[#111]">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-black border-b border-[#222]">
                  <tr className="text-[9px] font-mono tracking-widest text-zinc-500 uppercase">
                    <th className="p-2 text-left">Listing</th>
                    <th className="p-2 text-left">Live title</th>
                    <th className="p-2 text-left">Terms found</th>
                    <th className="p-2 text-left">Safe rewrite</th>
                    <th className="p-2 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {(audit.findings || []).filter(f => f.status === "risky").map((f) => (
                    <tr key={f.listing_id} className="border-b border-[#111] hover:bg-[#0a0a0a]"
                      data-testid={`etsy-custom-ip-row-${f.listing_id}`}>
                      <td className="p-2 align-top">
                        <a href={f.etsy_url} target="_blank" rel="noreferrer"
                          className="text-zinc-400 hover:text-[var(--tc-red)] font-mono text-[10px] flex items-center gap-1">
                          {f.listing_id} <ExternalLink size={10} />
                        </a>
                        {(f.detected_by || []).length > 0 && (
                          <div className="flex gap-1 mt-1">
                            {(f.detected_by || []).map((d) => (
                              <span key={d} className={`text-[8px] font-mono px-1 py-0.5 border ${d === "ai" ? "text-purple-400 border-purple-500/40" : "text-yellow-400 border-yellow-500/40"}`}>{d}</span>
                            ))}
                            {f.ai?.cached && (
                              <span title="AI verdict served from cache — zero LLM tokens"
                                className="text-[8px] font-mono px-1 py-0.5 border text-cyan-400 border-cyan-500/40">cached</span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="p-2 text-white text-[11px] max-w-[260px] align-top">
                        <div className="truncate" title={f.live_title}>{f.live_title}</div>
                      </td>
                      <td className="p-2 text-[10px] align-top">
                        <div className="flex flex-wrap gap-1 max-w-[240px]">
                          {[...(f.risky_terms_in_title || []), ...(f.risky_terms_in_description || []), ...(f.risky_tags || [])]
                            .slice(0, 3).map((t, i) => (
                              <span key={`r-${i}`} className="bg-[var(--tc-red)]/10 text-[var(--tc-red)] px-1.5 py-0.5 border border-[var(--tc-red)]/30 font-mono text-[9px]">{t}</span>
                            ))}
                          {(f.ai?.matches || []).slice(0, 2).map((m, i) => (
                            <span key={`ai-${i}`} title={m.explanation || ""}
                              className="bg-purple-500/10 text-purple-300 px-1.5 py-0.5 border border-purple-500/30 font-mono text-[9px]">
                              {m.references || m.term}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="p-2 text-green-400 text-[11px] max-w-[240px] align-top">
                        <div className="truncate" title={f.safe_title}>{f.safe_title}</div>
                      </td>
                      <td className="p-2 text-right align-top">
                        <button data-testid={`etsy-custom-ip-fix-${f.listing_id}`}
                          onClick={() => runFix([f.listing_id])}
                          disabled={busy}
                          className="btn-tc-secondary text-[10px] px-2 py-1 disabled:opacity-40">
                          Fix
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-xs text-green-400 flex items-center gap-2 font-mono">
              <ShieldCheck size={13} /> All {audit.counts?.scanned || 0} custom listings clean.
            </div>
          )}
        </>
      )}
    </div>
  );
}



// ─────────────────────────────────────────────────────────────────────
// Etsy Off-Platform Transactions compliance panel (iter119i)
// Scans every mapped listing's live description for URLs / "visit our
// store" language and one-click rewrites them using the compliant
// generator. Inactive/removed listings get moved back to draft.
// ─────────────────────────────────────────────────────────────────────
function OffPlatformCompliancePanel({ pw }) {
  const [scan, setScan] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [fixProgress, setFixProgress] = useState(null);
  const [forceToDraft, setForceToDraft] = useState(false);
  const [drafts, setDrafts] = useState(null);
  const [loadingDrafts, setLoadingDrafts] = useState(false);
  const [republishing, setRepublishing] = useState(false);
  const [republishProgress, setRepublishProgress] = useState(null);

  const runScan = async () => {
    setScanning(true);
    try {
      const { data } = await apiClient.get("/admin/etsy/off-platform/scan",
        { headers: { "X-Admin-Password": pw } });
      setScan(data);
      const c = data.counts || {};
      if ((c.violating || 0) === 0) {
        toast.success(
          `Compliance scan clean — ${c.scanned} listing(s) scanned, ` +
          `0 off-platform violations.`);
      } else {
        toast.warning(
          `Compliance scan — ${c.violating} of ${c.scanned} listings ` +
          `violate Etsy's Off-Platform Transactions policy ` +
          `(${c.inactive_or_removed} inactive/removed · ${c.active} active).`,
          { duration: 12000 });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Compliance scan failed");
    } finally { setScanning(false); }
  };

  const runFix = async () => {
    const violating = scan?.counts?.violating || 0;
    if (violating === 0 && !window.confirm(
      "No violations detected in the last scan. Re-run the fix anyway?"
    )) return;
    if (!window.confirm(
      `Fix ${violating} violating listing(s) on Etsy?\n\n` +
      `- Description will be rewritten with the Etsy-compliant version ` +
      `(no URL, no "visit our store" — routes bulk-order buyers into ` +
      `Etsy Messages instead).\n` +
      `- INACTIVE/REMOVED listings will be moved back to state=draft ` +
      `so you can review + republish.\n` +
      `- ACTIVE listings will be ` +
      `${forceToDraft ? "OVERRIDDEN — all going to draft" : "updated in place (stay live)"}.\n\n` +
      `This is a real Etsy write. Continue?`
    )) return;

    setFixing(true);
    setFixProgress({ completed: 0, total: violating, message: "Starting..." });
    try {
      const { data: kickoff } = await apiClient.post(
        "/admin/etsy/off-platform/fix",
        { force_to_draft: forceToDraft, only_violating: true },
        { headers: { "X-Admin-Password": pw } });
      const doc = await pollEtsyJob(kickoff.job_id, pw, (d) => {
        setFixProgress({
          completed: d.completed || 0,
          total: d.total || violating,
          message: d.message || "",
        });
      });
      const c = doc.summary?.counts || doc.counts || {};
      toast.success(
        `Compliance fix done — Fixed ${c.fixed || 0} · ` +
        `Moved to draft ${c.moved_to_draft || 0} · ` +
        `Errors ${(c.patch_error || 0) + (c.fetch_error || 0)}`,
        { duration: 12000 });
      setScan(null);
    } catch (e) {
      toast.error(e?.message || "Compliance fix failed");
    } finally {
      setFixing(false);
      setFixProgress(null);
    }
  };

  const loadDrafts = async () => {
    setLoadingDrafts(true);
    try {
      const { data } = await apiClient.get("/admin/etsy/off-platform/drafts",
        { headers: { "X-Admin-Password": pw } });
      setDrafts(data);
      if ((data.total || 0) === 0) {
        toast.info("No compliance-fixed drafts to republish.");
      } else {
        toast.success(`Found ${data.total} compliance-fixed draft(s) ready to republish.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load drafts");
    } finally { setLoadingDrafts(false); }
  };

  const runRepublish = async () => {
    const total = drafts?.total || 0;
    if (total === 0) {
      toast.info("Nothing to republish. Load drafts first.");
      return;
    }
    if (!window.confirm(
      `Republish ${total} draft(s) back to ACTIVE on Etsy?\n\n` +
      `Only drafts previously moved by the compliance fix will be touched. ` +
      `Etsy may reject any drafts missing images / inventory / price — ` +
      `those will be skipped and logged, the job will keep going.\n\n` +
      `Continue?`
    )) return;
    setRepublishing(true);
    setRepublishProgress({ completed: 0, total, message: "Starting..." });
    try {
      const { data: kickoff } = await apiClient.post(
        "/admin/etsy/off-platform/republish-drafts", {},
        { headers: { "X-Admin-Password": pw } });
      const doc = await pollEtsyJob(kickoff.job_id, pw, (d) => {
        setRepublishProgress({
          completed: d.completed || 0,
          total: d.total || total,
          message: d.message || "",
        });
      });
      const c = doc.summary?.counts || doc.counts || {};
      toast.success(
        `Republish done — Active ${c.republished || 0} · ` +
        `Skipped ${(c.not_draft || 0)} · ` +
        `Errors ${(c.activate_error || 0) + (c.fetch_error || 0)}`,
        { duration: 12000 });
      setDrafts(null);
    } catch (e) {
      toast.error(e?.message || "Republish failed");
    } finally {
      setRepublishing(false);
      setRepublishProgress(null);
    }
  };

  return (
    <div className="border border-red-500/40 bg-red-500/5 p-4 mb-4"
      data-testid="etsy-off-platform-panel">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="font-mono text-[10px] tracking-widest text-red-300 flex items-center gap-2">
          <ShieldAlert size={13} /> ETSY COMPLIANCE - Off-Platform Transactions sweep
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <label className="flex items-center gap-1.5 text-[10px] font-mono text-zinc-400 cursor-pointer"
            title="If checked, ALL fixed listings (including currently-active ones) will be moved to draft. Leave unchecked to patch active listings in place.">
            <input type="checkbox" checked={forceToDraft}
              onChange={(e) => setForceToDraft(e.target.checked)}
              data-testid="etsy-off-platform-force-draft" />
            Force everything to draft
          </label>
          <button data-testid="etsy-off-platform-scan-btn" onClick={runScan}
            disabled={scanning || fixing}
            className="btn-tc-secondary text-xs flex items-center gap-2 disabled:opacity-40">
            <Search size={12} />{scanning ? "Scanning..." : "Scan for violations"}
          </button>
          {scan && (scan.counts?.violating || 0) > 0 && (
            <button data-testid="etsy-off-platform-fix-btn" onClick={runFix}
              disabled={fixing || scanning}
              className="btn-tc-primary text-xs flex items-center gap-2 disabled:opacity-40">
              <Wand2 size={12} />
              {fixing ? "Fixing..." : `Fix All Violating (${scan.counts.violating})`}
            </button>
          )}
          <button data-testid="etsy-off-platform-load-drafts-btn"
            onClick={loadDrafts}
            disabled={loadingDrafts || fixing || republishing}
            className="btn-tc-secondary text-xs flex items-center gap-2 disabled:opacity-40"
            title="List every draft moved by the compliance sweep so you can republish them.">
            <Search size={12} />
            {loadingDrafts ? "Loading..." : "Find compliance drafts"}
          </button>
          {drafts && (drafts.total || 0) > 0 && (
            <button data-testid="etsy-off-platform-republish-btn"
              onClick={runRepublish}
              disabled={republishing || fixing}
              className="btn-tc-primary text-xs flex items-center gap-2 disabled:opacity-40">
              <ShieldCheck size={12} />
              {republishing ? "Republishing..." : `Republish all drafts (${drafts.total})`}
            </button>
          )}
        </div>
      </div>
      <div className="text-[10px] font-mono text-zinc-400 mb-3 leading-relaxed">
        Etsy takes down listings that link out to your own storefront or contain
        &ldquo;visit our store&rdquo; language. This sweep rewrites every violating
        description with the compliant template (bulk quotes go through Etsy
        Messages instead) and moves any inactive/removed listings back to draft.
      </div>
      {fixProgress && (
        <div className="text-[10px] font-mono text-red-300 mb-2"
          data-testid="etsy-off-platform-progress">
          {fixProgress.completed}/{fixProgress.total} - {fixProgress.message}
        </div>
      )}
      {republishProgress && (
        <div className="text-[10px] font-mono text-emerald-300 mb-2"
          data-testid="etsy-off-platform-republish-progress">
          {republishProgress.completed}/{republishProgress.total} - {republishProgress.message}
        </div>
      )}
      {drafts && (drafts.total || 0) > 0 && (
        <div className="mb-3 border border-emerald-500/30 bg-emerald-500/5 p-2"
          data-testid="etsy-off-platform-drafts-list">
          <div className="text-[10px] font-mono text-emerald-300 mb-1">
            {drafts.total} compliance-fixed draft(s) ready to republish
            {drafts.errors > 0 && (
              <span className="text-amber-400"> · {drafts.errors} lookup error(s)</span>
            )}
          </div>
          <div className="max-h-40 overflow-y-auto">
            <table className="w-full text-[10px] font-mono">
              <tbody>
                {drafts.drafts.slice(0, 100).map((d) => (
                  <tr key={d.listing_id} className="border-t border-emerald-500/10">
                    <td className="p-1 text-zinc-400">{d.listing_id}</td>
                    <td className="p-1 text-zinc-300">
                      <a href={d.url} target="_blank" rel="noreferrer"
                        className="hover:text-white truncate block max-w-[400px]">
                        {d.title || "(untitled)"}
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {drafts.drafts.length > 100 && (
              <div className="text-[10px] text-zinc-500 mt-1">
                +{drafts.drafts.length - 100} more…
              </div>
            )}
          </div>
        </div>
      )}
      {scan && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
            {[
              ["scanned", "Scanned", "text-zinc-300"],
              ["clean", "Clean", "text-green-400"],
              ["violating", "Violating", "text-red-400"],
              ["inactive_or_removed", "Inactive/Removed", "text-amber-400"],
              ["active", "Active", "text-emerald-400"],
            ].map(([k, l, tone]) => (
              <div key={k} className="border border-[#222] p-2 bg-black/40">
                <div className="text-[9px] font-mono text-zinc-500">{l}</div>
                <div className={`text-lg font-mono ${tone}`}>
                  {scan.counts?.[k] ?? 0}
                </div>
              </div>
            ))}
          </div>
          {(scan.counts?.violating || 0) > 0 ? (
            <div className="max-h-64 overflow-y-auto border border-[#222]"
              data-testid="etsy-off-platform-list">
              <table className="w-full text-xs font-mono">
                <thead className="bg-[#111] sticky top-0">
                  <tr>
                    <th className="text-left p-2 text-zinc-500">Listing</th>
                    <th className="text-left p-2 text-zinc-500">State</th>
                    <th className="text-left p-2 text-zinc-500">Violations</th>
                  </tr>
                </thead>
                <tbody>
                  {scan.findings.filter((f) => f.violations && f.violations.length > 0)
                    .map((f) => (
                      <tr key={f.listing_id} className="border-t border-[#1a1a1a]">
                        <td className="p-2 text-zinc-300 align-top">
                          <a href={f.url} target="_blank" rel="noreferrer"
                            className="hover:text-white truncate block max-w-[300px]">
                            {f.title || f.listing_id}
                          </a>
                        </td>
                        <td className={`p-2 align-top text-[10px] ${
                          f.state === "active" ? "text-emerald-400"
                            : (f.state === "inactive" || f.state === "removed")
                              ? "text-amber-400" : "text-zinc-500"}`}>
                          {f.state?.toUpperCase()}
                        </td>
                        <td className="p-2 text-red-300 align-top text-[10px]">
                          {f.violations.slice(0, 3).map((v, i) => (
                            <div key={i} className="truncate max-w-[400px]">{v}</div>
                          ))}
                          {f.violations.length > 3 && (
                            <div className="text-zinc-500">
                              +{f.violations.length - 3} more
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-xs text-green-400 flex items-center gap-2 font-mono">
              <ShieldCheck size={13} />
              All {scan.counts?.scanned || 0} listings are Off-Platform compliant.
            </div>
          )}
        </>
      )}
    </div>
  );
}



// ─────────────────────────────────────────────────────────────────────
// iter119j — Etsy diagnostics + nuke-and-rebuild + reverse state sync
// ─────────────────────────────────────────────────────────────────────
function EtsyDiagnosticsPanel({ pw }) {
  const [listingId, setListingId] = useState("");
  const [listingReport, setListingReport] = useState(null);
  const [listingBusy, setListingBusy] = useState(false);

  const [chartTestId, setChartTestId] = useState("");
  const [chartTestReport, setChartTestReport] = useState(null);
  const [chartTestBusy, setChartTestBusy] = useState(false);

  const [rebuildIds, setRebuildIds] = useState("");
  const [rebuildBusy, setRebuildBusy] = useState(false);
  const [rebuildProgress, setRebuildProgress] = useState(null);

  const [reconcileBusy, setReconcileBusy] = useState(false);
  const [reconcileProgress, setReconcileProgress] = useState(null);
  const [mappedRows, setMappedRows] = useState(null);
  const [mappedBusy, setMappedBusy] = useState(false);

  const [receiptId, setReceiptId] = useState("");
  const [receiptReport, setReceiptReport] = useState(null);
  const [receiptBusy, setReceiptBusy] = useState(false);
  const [resendBusy, setResendBusy] = useState(false);

  const runListingDiagnose = async () => {
    if (!listingId.trim()) return;
    setListingBusy(true); setListingReport(null);
    try {
      const { data } = await apiClient.get(
        `/admin/etsy/off-platform/diagnose/${listingId.trim()}`,
        { headers: { "X-Admin-Password": pw } });
      setListingReport(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Diagnose failed");
    } finally { setListingBusy(false); }
  };

  const runChartTest = async () => {
    if (!chartTestId.trim()) return;
    setChartTestBusy(true); setChartTestReport(null);
    try {
      const { data } = await apiClient.post(
        `/admin/etsy/charts/test/${chartTestId.trim()}`, {},
        { headers: { "X-Admin-Password": pw } });
      setChartTestReport(data);
      if (data.ok && data.uploaded > 0) {
        toast.success(`Uploaded ${data.uploaded} chart image(s)`);
      } else if (data.ok) {
        toast.warning(`0 charts uploaded — see trace below`);
      } else {
        toast.error(data.detail || "Chart test failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Chart test failed");
    } finally { setChartTestBusy(false); }
  };

  const runRebuild = async () => {
    const ids = rebuildIds.split(/[\s,]+/).map((s) => s.trim())
                            .filter(Boolean);
    if (ids.length === 0) { toast.error("Paste at least one listing ID"); return; }
    if (!window.confirm(
      `DESTRUCTIVE - DELETE ${ids.length} listing(s) on Etsy and recreate ` +
      `each one as a fresh draft?\n\nBuyer favourites and reviews on the ` +
      `old listings will be lost. New drafts will use the compliant ` +
      `description generator.\n\nContinue?`
    )) return;
    setRebuildBusy(true);
    setRebuildProgress({ completed: 0, total: ids.length, message: "Starting..." });
    try {
      const { data: kickoff } = await apiClient.post(
        "/admin/etsy/off-platform/nuke-and-rebuild",
        { listing_ids: ids },
        { headers: { "X-Admin-Password": pw } });
      const doc = await pollEtsyJob(kickoff.job_id, pw, (d) => {
        setRebuildProgress({
          completed: d.completed || 0,
          total: d.total || ids.length,
          message: d.message || "",
        });
      });
      const c = doc.summary?.counts || doc.counts || {};
      toast.success(
        `Rebuild done - Rebuilt ${c.rebuilt || 0} · ` +
        `Delete errors ${c.delete_error || 0} · ` +
        `Recreate errors ${(c.recreate_error || 0) + (c.recreate_incomplete || 0)}`,
        { duration: 15000 });
      setRebuildIds("");
    } catch (e) {
      toast.error(e?.message || "Rebuild failed");
    } finally {
      setRebuildBusy(false);
      setRebuildProgress(null);
    }
  };

  const runReconcile = async () => {
    setReconcileBusy(true);
    setReconcileProgress({ completed: 0, total: 0, message: "Starting..." });
    try {
      const { data: kickoff } = await apiClient.post(
        "/admin/etsy/reconcile-states", {},
        { headers: { "X-Admin-Password": pw } });
      const doc = await pollEtsyJob(kickoff.job_id, pw, (d) => {
        setReconcileProgress({
          completed: d.completed || 0,
          total: d.total || 0,
          message: d.message || "",
        });
      });
      const c = doc.summary?.counts || doc.counts || {};
      toast.success(
        `Reconcile done - Active ${c.active || 0} · Draft ${c.draft || 0} · ` +
        `Inactive ${c.inactive || 0} · Gone ${c.gone_404 || 0}`,
        { duration: 12000 });
      await loadMapped();
    } catch (e) {
      toast.error(e?.message || "Reconcile failed");
    } finally {
      setReconcileBusy(false);
      setReconcileProgress(null);
    }
  };

  const loadMapped = async (state = null) => {
    setMappedBusy(true);
    try {
      const url = state ? `/admin/etsy/mapped-listings?state=${state}` :
                            "/admin/etsy/mapped-listings";
      const { data } = await apiClient.get(url,
        { headers: { "X-Admin-Password": pw } });
      setMappedRows(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    } finally { setMappedBusy(false); }
  };

  const prefillInactiveIds = () => {
    if (!mappedRows?.rows?.length) {
      toast.error("Load a state first — click 'Load stuck (edit)' or 'Load inactive'");
      return;
    }
    // Prefill with EVERY currently-loaded row rather than filtering
    // by state again. Etsy's "Inactive" bucket in the seller UI is a
    // grab-bag that includes state=edit (post-edit review), not just
    // state=inactive, so an inner filter would zero out the 88 stuck
    // "Violated" listings which live in `edit`.
    const ids = mappedRows.rows.map((r) => r.etsy_listing_id).join("\n");
    setRebuildIds(ids);
    toast.success(`Prefilled ${mappedRows.rows.length} listing ID(s) into rebuild`);
  };

  const runReceiptDiagnose = async () => {
    if (!receiptId.trim()) return;
    setReceiptBusy(true); setReceiptReport(null);
    try {
      const { data } = await apiClient.get(
        `/admin/etsy/orders/diagnose/${receiptId.trim()}`,
        { headers: { "X-Admin-Password": pw } });
      setReceiptReport(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Diagnose failed");
    } finally { setReceiptBusy(false); }
  };

  const runResend = async () => {
    if (!receiptId.trim()) return;
    setResendBusy(true);
    try {
      const { data } = await apiClient.post(
        `/admin/etsy/orders/resend-email/${receiptId.trim()}`, {},
        { headers: { "X-Admin-Password": pw } });
      if (data.ok) {
        toast.success(`Admin email re-sent for receipt ${receiptId.trim()}`);
        await runReceiptDiagnose();
      } else {
        toast.error(data.error || "Resend failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resend failed");
    } finally { setResendBusy(false); }
  };

  return (
    <div className="border border-cyan-500/30 bg-cyan-500/5 p-4 mb-4"
      data-testid="etsy-diagnostics-panel">
      <div className="font-mono text-[10px] tracking-widest text-cyan-300 flex items-center gap-2 mb-3">
        <Stethoscope size={13} /> ETSY DIAGNOSTICS
      </div>

      <div className="mb-4 border border-[#222] p-3 bg-black/40">
        <div className="text-[10px] font-mono text-zinc-500 mb-2">
          1. Diagnose one listing - read-only forensic dump
        </div>
        <div className="flex gap-2 flex-wrap">
          <input type="text" value={listingId}
            onChange={(e) => setListingId(e.target.value)}
            placeholder="Etsy listing ID (e.g. 4556973369)"
            data-testid="etsy-diagnose-listing-input"
            className="flex-1 min-w-[220px] px-2 py-1 bg-black border border-[#222] text-xs font-mono text-white focus:border-cyan-500 outline-none" />
          <button data-testid="etsy-diagnose-listing-btn"
            onClick={runListingDiagnose} disabled={listingBusy || !listingId.trim()}
            className="btn-tc-secondary text-xs flex items-center gap-1 disabled:opacity-40">
            <Search size={11} /> {listingBusy ? "Fetching..." : "Diagnose"}
          </button>
        </div>
        {listingReport && (
          <div className="mt-2 border border-[#222] p-2 bg-black/50 text-[10px] font-mono text-zinc-300 overflow-auto max-h-96"
            data-testid="etsy-diagnose-listing-report">
            <div>Product ID: <span className="text-white">{listingReport.product_id || "-"}</span>
              {" · "}Ready-Made: {listingReport.is_ready_made ? "yes" : "no"}</div>
            {listingReport.live ? (
              <>
                <div>Live state: <span className="text-white">{listingReport.live.state?.toUpperCase()}</span>
                  {" · "}Title: <span className="text-white">{listingReport.live.title}</span></div>
                <div className={listingReport.live.violations?.length
                  ? "text-red-300 mt-1" : "text-emerald-400 mt-1"}>
                  Violations: {listingReport.live.violations?.length
                    ? listingReport.live.violations.join(" · ") : "none"}
                </div>
                <div className="mt-1 text-zinc-500">Description excerpt:</div>
                <div className="whitespace-pre-wrap text-zinc-400">
                  {listingReport.live.description_excerpt || ""}</div>
              </>
            ) : listingReport.live_error ? (
              <div className="text-red-300">
                Live fetch failed: {listingReport.live_error.error}
              </div>
            ) : null}
            {listingReport.last_compliance_fix_at && (
              <div className="mt-2 text-zinc-500">
                Last compliance fix: {listingReport.last_compliance_fix_at}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mb-4 border border-amber-500/30 p-3 bg-black/40">
        <div className="text-[10px] font-mono text-amber-300 mb-2">
          1b. Test chart upload - run <code>_refresh_chart_images</code> against
          one listing and see every step (list → delete stale → render → upload)
        </div>
        <div className="text-[10px] text-zinc-500 mb-2">
          Uses one real Etsy round-trip. No full sync required. Shows the
          diagnostic string that a normal Update Drafts run now surfaces in
          the sync log.
        </div>
        <div className="flex gap-2 flex-wrap">
          <input type="text" value={chartTestId}
            onChange={(e) => setChartTestId(e.target.value)}
            placeholder="Etsy listing ID (must be mapped, e.g. 4559127669)"
            data-testid="etsy-chart-test-input"
            className="flex-1 min-w-[220px] px-2 py-1 bg-black border border-[#222] text-xs font-mono text-white focus:border-amber-500 outline-none" />
          <button data-testid="etsy-chart-test-btn"
            onClick={runChartTest} disabled={chartTestBusy || !chartTestId.trim()}
            className="btn-tc-secondary text-xs flex items-center gap-1 disabled:opacity-40">
            <Search size={11} /> {chartTestBusy ? "Running..." : "Test chart upload"}
          </button>
        </div>
        {chartTestReport && (
          <div className="mt-2 border border-[#222] p-2 bg-black/50 text-[10px] font-mono overflow-auto max-h-96"
            data-testid="etsy-chart-test-report">
            <div className="text-zinc-300">
              Listing: <span className="text-white">{chartTestReport.listing_id}</span>
              {" · "}SKU: <span className="text-white">{chartTestReport.product_sku || "-"}</span>
              {" · "}Uploaded: <span className={chartTestReport.uploaded > 0
                ? "text-emerald-300" : "text-red-300"}>
                {chartTestReport.uploaded}
              </span>
            </div>
            <div className="mt-2 text-zinc-500">Trace:</div>
            <div className="whitespace-pre-wrap text-zinc-300 mt-1">
              {chartTestReport.detail || "(no detail)"}
            </div>
          </div>
        )}
      </div>

      <div className="mb-4 border border-red-500/30 p-3 bg-black/40">
        <div className="text-[10px] font-mono text-red-300 mb-2 flex items-center gap-1">
          <Bomb size={11} /> 2. NUKE &amp; REBUILD - for permanently-flagged listings
        </div>
        <div className="text-[10px] text-zinc-500 mb-2">
          DELETEs each listing on Etsy then recreates it as a fresh draft with the
          compliant description generator. Buyer favourites on the old listings will
          be lost. Use for stuck &ldquo;Violated Inactive&rdquo; listings.
        </div>
        <textarea value={rebuildIds}
          onChange={(e) => setRebuildIds(e.target.value)}
          placeholder="Paste listing IDs - comma, space or newline separated"
          data-testid="etsy-rebuild-ids-input"
          rows={2}
          className="w-full px-2 py-1 bg-black border border-[#222] text-xs font-mono text-white focus:border-red-500 outline-none mb-2" />
        <button data-testid="etsy-rebuild-btn" onClick={runRebuild}
          disabled={rebuildBusy || !rebuildIds.trim()}
          className="btn-tc-primary text-xs flex items-center gap-1 disabled:opacity-40">
          <Bomb size={11} /> {rebuildBusy ? "Rebuilding..." : "Nuke & rebuild"}
        </button>
        {rebuildProgress && (
          <div className="text-[10px] font-mono text-red-300 mt-2"
            data-testid="etsy-rebuild-progress">
            {rebuildProgress.completed}/{rebuildProgress.total} - {rebuildProgress.message}
          </div>
        )}
      </div>

      <div className="mb-4 border border-[#222] p-3 bg-black/40">
        <div className="text-[10px] font-mono text-zinc-500 mb-2 flex items-center gap-1">
          <Repeat size={11} /> 3. Pull Etsy state - mirror into admin CMS
        </div>
        <div className="text-[10px] text-zinc-500 mb-2">
          Read-only mirror. If you flip a listing draft→active on Etsy this pulls
          it back so the admin product row shows the new state as a badge.
        </div>
        <button data-testid="etsy-reconcile-states-btn" onClick={runReconcile}
          disabled={reconcileBusy}
          className="btn-tc-secondary text-xs flex items-center gap-1 disabled:opacity-40">
          <RefreshCw size={11} /> {reconcileBusy ? "Reconciling..." : "Reconcile Etsy states"}
        </button>
        <button data-testid="etsy-load-stuck-edit-btn"
          onClick={() => loadMapped("edit")} disabled={mappedBusy}
          className="btn-tc-secondary text-xs flex items-center gap-1 disabled:opacity-40 ml-2"
          title="The 'Inactive N' count in Etsy's seller UI is mostly state=edit (post-edit policy review). Load those.">
          <ListChecks size={11} /> {mappedBusy ? "Loading..." : "Load stuck (edit)"}
        </button>
        <button data-testid="etsy-load-inactive-btn"
          onClick={() => loadMapped("inactive")} disabled={mappedBusy}
          className="btn-tc-secondary text-xs flex items-center gap-1 disabled:opacity-40 ml-2">
          <ListChecks size={11} /> {mappedBusy ? "Loading..." : "Load inactive"}
        </button>
        {mappedRows && mappedRows.rows.length > 0 && (
          <button data-testid="etsy-prefill-inactive-btn"
            onClick={prefillInactiveIds}
            className="btn-tc-primary text-xs flex items-center gap-1 ml-2"
            title="Copies every currently-loaded listing ID into the Nuke & Rebuild box.">
            <Bomb size={11} /> Prefill {mappedRows.rows.length} ID(s) into rebuild
          </button>
        )}
        {reconcileProgress && (
          <div className="text-[10px] font-mono text-cyan-300 mt-2"
            data-testid="etsy-reconcile-progress">
            {reconcileProgress.completed}/{reconcileProgress.total} - {reconcileProgress.message}
          </div>
        )}
        {mappedRows && (
          <div className="mt-2 border border-[#222] p-2 bg-black/50"
            data-testid="etsy-mapped-listings">
            <div className="text-[10px] font-mono text-zinc-500 mb-1">
              {mappedRows.total} mapping(s) · counts:{" "}
              {Object.entries(mappedRows.counts).map(([k, v]) =>
                <span key={k} className="mr-2 text-white">{k}={v}</span>)}
            </div>
            <div className="max-h-56 overflow-y-auto">
              <table className="w-full text-[10px] font-mono">
                <tbody>
                  {mappedRows.rows.slice(0, 200).map((r) => (
                    <tr key={r.etsy_listing_id} className="border-t border-[#1a1a1a]">
                      <td className="p-1 text-zinc-400">{r.etsy_listing_id}</td>
                      <td className={`p-1 ${
                        r.etsy_state === "active" ? "text-emerald-400"
                          : r.etsy_state === "draft" ? "text-cyan-400"
                          : r.etsy_state === "inactive" ? "text-amber-400"
                          : r.etsy_state === "gone" ? "text-red-400"
                          : "text-zinc-500"}`}>
                        {(r.etsy_state || "?").toUpperCase()}
                      </td>
                      <td className="p-1 text-zinc-500 truncate max-w-[240px]">
                        {r.internal_product_id}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {mappedRows.rows.length > 200 && (
                <div className="text-[10px] text-zinc-500 mt-1">
                  +{mappedRows.rows.length - 200} more...
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="border border-[#222] p-3 bg-black/40">
        <div className="text-[10px] font-mono text-zinc-500 mb-2 flex items-center gap-1">
          <Mail size={11} /> 4. Diagnose one Etsy receipt / resend admin email
        </div>
        <div className="flex gap-2 flex-wrap">
          <input type="text" value={receiptId}
            onChange={(e) => setReceiptId(e.target.value)}
            placeholder="Etsy receipt ID (e.g. 4149323281)"
            data-testid="etsy-diagnose-receipt-input"
            className="flex-1 min-w-[220px] px-2 py-1 bg-black border border-[#222] text-xs font-mono text-white focus:border-cyan-500 outline-none" />
          <button data-testid="etsy-diagnose-receipt-btn"
            onClick={runReceiptDiagnose} disabled={receiptBusy || !receiptId.trim()}
            className="btn-tc-secondary text-xs flex items-center gap-1 disabled:opacity-40">
            <Search size={11} /> {receiptBusy ? "Fetching..." : "Diagnose"}
          </button>
          <button data-testid="etsy-resend-receipt-btn"
            onClick={runResend} disabled={resendBusy || !receiptId.trim()}
            className="btn-tc-primary text-xs flex items-center gap-1 disabled:opacity-40">
            <Mail size={11} /> {resendBusy ? "Sending..." : "Resend admin email"}
          </button>
        </div>
        {receiptReport && (
          <div className="mt-2 border border-[#222] p-2 bg-black/50 text-[10px] font-mono text-zinc-300 overflow-auto max-h-72"
            data-testid="etsy-diagnose-receipt-report">
            <div>In local orders: <span className={receiptReport.in_local_orders
              ? "text-emerald-400" : "text-red-400"}>{String(receiptReport.in_local_orders)}</span>
              {receiptReport.order_id && <> · Order: <span className="text-white">{receiptReport.order_id}</span></>}</div>
            <div>Email sent: <span className={receiptReport.email_sent
              ? "text-emerald-400" : "text-red-400"}>{String(receiptReport.email_sent)}</span>
              {receiptReport.email_sent_at && <> · at {receiptReport.email_sent_at}</>}</div>
            {receiptReport.email_error && (
              <div className="text-red-300">Email error: {receiptReport.email_error}</div>
            )}
            <div className="mt-1">Watermark: <span className="text-white">{receiptReport.watermark_iso || "-"}</span>
              {" · "}Last run: <span className="text-white">{receiptReport.last_run_at || "-"}</span></div>
            <div>Live Etsy created at: <span className="text-white">{receiptReport.live_created_iso || "-"}</span>
              {" · "}Paid: {String(receiptReport.live_is_paid)}</div>
            <div>Would next poll pick it up? <span className={receiptReport.would_be_picked_up
              ? "text-emerald-400" : "text-red-400"}>{String(receiptReport.would_be_picked_up)}</span></div>
            {receiptReport.live_error && (
              <div className="text-red-300 mt-1">Live fetch error: {receiptReport.live_error}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
